import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    X, Mail, MessageCircle, Mic2, FileText, Upload,
    ChevronDown, Trash2, Plus, UserPlus, Search, MapPin,
    CircleCheckBig, SquareCheckBig, CalendarCheck, BadgeCheck, LayoutList, Phone,
    Loader2, Building2, User, Calendar, ClipboardList, Package, UserCheck, IndianRupee, Image as ImageIcon
} from 'lucide-react';
import './MassOperation.css';
import Swal from 'sweetalert2';
import API_ENDPOINTS from '../../../config/apiEndpoints';
import { getSession } from '../../../getSession';
import Panels from '../Panels/Panels';
import AddEnquiryForm from '../../../pages/Enquiries/Enquiries/components/AddEnquiryForm';
import AddLeadForm from '../../LeadForm/AddLeadForm';
import MediaLibrary from '../../MediaLibrary';
import { bindSourceMediumCampaign } from '../../../utils/lead';

const formatDateTimeDisplay = (dateStr, timeStr) => {
    if (!dateStr) return "--";
    const monthsAbbr = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const d = new Date(dateStr);
    return `${d.getDate().toString().padStart(2, '0')}-${monthsAbbr[d.getMonth()]}-${d.getFullYear()} ${timeStr || ''}`;
};

const API_BASE_SERVICES = (process.env.REACT_APP_SERVICES_AZURE_BASEURL || '').replace(/\/$/, '');
const API_SERVICES = (process.env.REACT_APP_SERVICES_API_BASE_URL || '').replace(/\/$/, '');
const LOCAL_BASE_URL = (process.env.REACT_APP_SERVICES_API_BASE_URL || '').replace(/\/$/, '');
const WCF_BASE = (process.env.REACT_APP_WCF_API_BASE_URL || '').replace(/\/$/, '');
 const NEWV3_BASE_URL = (process.env.REACT_APP_SERVICES_API_BASE_URL || '').replace(/\/$/, '');
const SERVICE_AZURE_URL = API_BASE_SERVICES;

const TOKEN = (process.env.REACT_APP_TOKE_UNIVERSAL_TOKEN || '-2295521862261168');
const Related_TO_Suggestion = `${API_SERVICES}/${API_ENDPOINTS.TASKS.RELATED_TO_SUGGETION}`;
const Related_TO_Suggestion_For_Enquiry = `${API_BASE_SERVICES}/${API_ENDPOINTS.APPOINTMENTS.RELATED_TO_SUGGETION}`;
const apiFollowup_List = `${API_BASE_SERVICES}/${API_ENDPOINTS.APPOINTMENTS.FOLLOWUPSTATUS_LIST}`;
 const Get_Enquiry_Data = `${NEWV3_BASE_URL}${API_ENDPOINTS.ENQUIRIES.ENQUIRY_DETAILS_LIST}`;
const Get_User_Hierarchy_List = `${API_BASE_SERVICES}/${API_ENDPOINTS.APPOINTMENTS.GET_USER_HIRERARCHY_LIST}`;
const Product_Suggestion_API = `${API_SERVICES}/${API_ENDPOINTS.APPOINTMENTS.PRODUCT_SUGGESTIONS}`;

const LeadDetails = `${NEWV3_BASE_URL}${API_ENDPOINTS.COMMON.GET_LEAD_BY_ID}`;
const FROM_MAIL_IDS = `${API_SERVICES}${API_ENDPOINTS.COMMON.NEW_GET_FROM_MAIL_ID}`;

/**
 * MassOperationPanel - The bar that appears when records are selected
 */
export const MassOperationPanel = ({
    selectedCount,
    totalCount,
    onClose,
    onGo,
    variant = 'header'
}) => {
    const [selectedAction, setSelectedAction] = useState('');
    const [showEntityMenu, setShowEntityMenu] = useState(false);
    const plusBtnRef = useRef(null);

    const actions = [
        { value: 'mass_update', label: 'Mass Update' }
    ];

    return (
        <motion.div
            initial={variant === "floating" ? { opacity: 0, y: 20 } : { opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={variant === "floating" ? { opacity: 0, y: 20 } : { opacity: 0, y: -10 }}
            className={`mass-operation-panel-central ${variant === 'header' ? 'header-variant' : ''}`}
        >
            <div className="flex items-center gap-4 relative">
                <div className="selection-badge-premium">
                    <span className="badge-count">{selectedCount}</span>
                    <span className="badge-text uppercase font-bold tracking-wider">Selected of {totalCount}</span>
                </div>

                <div className="flex items-center gap-2">
                    <select
                        value={selectedAction}
                        onChange={(e) => setSelectedAction(e.target.value)}
                        className="mass-action-dropdown-premium min-w-[200px]"
                    >
                        <option value="">Select Mass Operation</option>
                        {actions.map(action => (
                            <option key={action.value} value={action.value}>
                                {action.label}
                            </option>
                        ))}
                    </select>

                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => onGo(selectedAction)}
                            disabled={!selectedAction}
                            className="go-button-premium uppercase"
                        >
                            GO
                        </button>
                    </div>
                </div>


            </div>

            <div className="flex items-center">
                <button
                    onClick={onClose}
                    className="p-2 hover:bg-gray-100 rounded-full text-gray-400 hover:text-gray-600 transition-all"
                    title="Clear selection"
                >
                    <X className="w-5 h-5" />
                </button>
            </div>
        </motion.div>
    );
};

/**
 * MassEntityActionMenu - The "Entity Action" menu 
 */
export const MassEntityActionMenu = ({
    onAction,
    onClose,
    isOpen = true,
    variant = 'floating',
    hideHeader = false,
    crmOperationPerms = {}
}) => {
    const [dynamicPerms, setDynamicPerms] = useState(null);

    useEffect(() => {
        if (!isOpen) return;

        const fetchDynamicPerms = async () => {
            try {
                // Check session storage first
                const cached = sessionStorage.getItem('dynamicActionPerms');
                if (cached) {
                    setDynamicPerms(JSON.parse(cached));
                    return;
                }

                const { TokenId, userId } = getSession();
                const token = sessionStorage.getItem('deviceToken') || TokenId || "-2295521862261168";
                const uid = userId || "34594";
                const API_BASE = (process.env.REACT_APP_SERVICES_AZURE_BASEURL || 'https://newcrmapi.kit19.com').replace(/\/$/, '');
                const url = `${API_BASE}${API_ENDPOINTS.COMMON.GET_ALL_PERMISSION_BY_MODULE_CODE}`;
                
                const response = await fetch(url, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        Token: String(token),
                        Details: {
                            Mode: "S",
                            ModuleCode: "PAG10031,PAG10002",
                            UserId: String(uid)
                        }
                    })
                });

                if (response.ok) {
                    const data = await response.json();
                    const list = data?.Details || data?.d || data?.Data || data?.data || data || [];
                    let parsedList = [];
                    if (Array.isArray(list)) {
                        parsedList = list;
                    } else if (typeof list === 'string') {
                        try { parsedList = JSON.parse(list); } catch(e) {}
                    }
                    
                    const permsObj = {
                        lead: parsedList.find(p => p.ModuleCode === 'PAG10031') || {},
                        enquiry: parsedList.find(p => p.ModuleCode === 'PAG10002') || {}
                    };
                    sessionStorage.setItem('dynamicActionPerms', JSON.stringify(permsObj));
                    setDynamicPerms(permsObj);
                }
            } catch (err) {
                console.error("Error fetching dynamic perms:", err);
            }
        };

        fetchDynamicPerms();
    }, [isOpen]);

    if (isOpen === false) return null;

    const checkPerm = (permName) => {
        if (dynamicPerms) {
            // Return true if at least ONE of them has permission
            return (dynamicPerms.lead[permName] !== false) || (dynamicPerms.enquiry[permName] !== false);
        }
        return crmOperationPerms[permName] !== false; // fallback
    };

    const actions = [
        { id: 'addlead', icon: UserPlus, label: 'Add Lead' },
        { id: 'addenquiry', icon: Search, label: 'Add Enquiry' },
        { id: 'followup', icon: CircleCheckBig, label: 'Add Followup' },
        { id: 'mail', icon: Mail, label: 'Send Mail' },
        { id: 'voice', icon: Phone, label: 'Send Voice' },
        { id: 'upload', icon: Upload, label: 'Upload Document' },
        { id: 'addtask', icon: SquareCheckBig, label: 'Add Task' },
        { id: 'addappointment', icon: CalendarCheck, label: 'Add Appointment' },
        { id: 'adddeal', icon: BadgeCheck, label: 'Add Deal' },
        { id: 'fillWebform', icon: LayoutList, label: 'Fill Webform' },
    ].filter(action => {
        switch (action.id) {
            case 'followup': return checkPerm('AddFollowup');
            case 'mail': return checkPerm('SendMail');
            case 'voice': return checkPerm('SendVoice');
            case 'upload': return checkPerm('UploadDocument');
            case 'addtask': return checkPerm('AddTask');
            case 'addappointment': return checkPerm('AddAppointment');
            case 'adddeal': return checkPerm('AddDeal');
            case 'fillWebform': return checkPerm('AddWebform');
            default: return true;
        }
    });

    return (
        <motion.div
            initial={variant === 'drawer' ? { x: '100%' } : { opacity: 0, y: -10, scale: 0.95 }}
            animate={variant === 'drawer' ? { x: 0 } : { opacity: 1, y: 0, scale: 1 }}
            exit={variant === 'drawer' ? { x: '100%' } : { opacity: 0, y: -10, scale: 0.95 }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className={`mass-entity-action-panel ${variant === 'floating' ? 'floating-variant' :
                    variant === 'drawer' ? 'drawer-variant' :
                        'header-panel-variant'
                }`}
            onClick={e => e.stopPropagation()}
        >
            {!hideHeader && (
                <div className="panel-header-compact">
                    <h3 className="panel-title-compact">Create New Record</h3>
                    <button className="panel-close-btn-compact" onClick={onClose}>
                        <X size={16} />
                    </button>
                </div>
            )}

            <div className="panel-sub-label-compact">
                Entity Action
            </div>

            <div className={variant === 'drawer' ? 'panel-action-list-vertical' : 'panel-action-grid'}>
                {actions.map(action => (
                    <button
                        key={action.id}
                        className={variant === 'drawer' ? 'panel-action-item-vertical' : 'panel-action-tile'}
                        onClick={() => {
                            onAction(action.id);
                            onClose();
                        }}
                    >
                        <div className={variant === 'drawer' ? 'item-icon-box' : 'tile-icon-wrapper'}>
                            <action.icon size={variant === 'drawer' ? 18 : 20} />
                        </div>
                        <span className={variant === 'drawer' ? 'item-text-vertical' : 'tile-text'}>{action.label}</span>
                    </button>
                ))}
            </div>

            <div className={variant === 'drawer' ? 'panel-footer-drawer' : 'panel-footer-compact'}>
                <button className={variant === 'drawer' ? 'panel-cancel-btn-drawer' : 'panel-cancel-btn-compact'} onClick={onClose}>
                    Cancel
                </button>
            </div>
        </motion.div>
    );
};

/**
 * MassOperationDrawer - Unified side drawer for all CRM operations
 */
export const MassOperationDrawer = ({
    isOpen,
    onClose,
    operationMode = 'followup',
    selectedRecord: externalSelectedRecord,
    associatedTo: externalAssociatedTo = 'Lead',
    selectedCount = 0
}) => {
    // Stage 1: Entity Choice & Record Selection
    const [associatedTo, setAssociatedTo] = useState('Lead');
    const [searchQuery, setSearchQuery] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const searchTimerRef = useRef(null);
    const sidebarSearchRef = useRef(null);

    const cachedPermsRaw = typeof window !== 'undefined' ? sessionStorage.getItem('dynamicActionPerms') : null;
    let dynamicPerms = null;
    try { if (cachedPermsRaw) dynamicPerms = JSON.parse(cachedPermsRaw); } catch(e) {}

    const getPermNameForMode = (mode) => {
        switch(mode) {
            case 'followup': return 'AddFollowup';
            case 'mail': return 'SendMail';
            case 'voice': return 'SendVoice';
            case 'upload': return 'UploadDocument';
            case 'addtask': return 'AddTask';
            case 'addappointment': return 'AddAppointment';
            case 'adddeal': return 'AddDeal';
            case 'fillWebform': return 'AddWebform';
            default: return null;
        }
    };
    
    const permName = getPermNameForMode(operationMode);
    const leadEnabled = permName && dynamicPerms && dynamicPerms.lead ? dynamicPerms.lead[permName] !== false : true;
    const enquiryEnabled = permName && dynamicPerms && dynamicPerms.enquiry ? dynamicPerms.enquiry[permName] !== false : true;
    
    useEffect(() => {
        if (isOpen && permName && dynamicPerms) {
            if (associatedTo === 'Enquiry' && !enquiryEnabled && leadEnabled) {
                setAssociatedTo('Lead');
            } else if (associatedTo === 'Lead' && !leadEnabled && enquiryEnabled) {
                setAssociatedTo('Enquiry');
            }
        }
    }, [isOpen, operationMode, enquiryEnabled, leadEnabled]);

    // Shared UI State
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoadingInitialData, setIsLoadingInitialData] = useState(false);

    // --- OPERATION SPECIFIC STATES ---

    // 1. Followup
    const [followupStatuses, setFollowupStatuses] = useState([]);
    const [userHierarchy, setUserHierarchy] = useState([]);
    const [productSearch, setProductSearch] = useState('');
    const [productSuggestions, setProductSuggestions] = useState([]);

    // 2. Add Lead
    const [leadForm, setLeadForm] = useState({
        PersonName: '',
        CompanyName: '',
        Mobile1: '',
        Email1: '',
        Source: '',
        Medium: '',
        Campaign: '',
        EnquiryTags: '',
        Remarks: '',
        ResiAddress: '',
        OfficeAddress: '',
        PinCode: '0'
    });
    const [sourceOptions, setSourceOptions] = useState([]);
    const [mediumOptions, setMediumOptions] = useState([]);
    const [campaignOptions, setCampaignOptions] = useState([]);
    const [dialCodes, setDialCodes] = useState([]);
    const [selectedDialCode, setSelectedDialCode] = useState('+91');
    const [isProductLoading, setIsProductLoading] = useState(false);
    const [isProductDropdownOpen, setIsProductDropdownOpen] = useState(false);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const productRef = useRef(null);
    const [followupForm, setFollowupForm] = useState({
        status: '',
        assignedUser: '',
        dueDate: new Date().toISOString().slice(0, 16).replace('T', ' '),
        remarks: '',
        isReAssign: false
    });

    // 2. Mail
    const [mailAccounts, setMailAccounts] = useState([]);
    const [fromMailOptions, setFromMailOptions] = useState([]);
    const [mailTemplates, setMailTemplates] = useState([]);
    const [leadEmails, setLeadEmails] = useState([]);
    const [mailData, setMailData] = useState({
        ChooseEmails: [],
        Account: '',
        From: '',
        ReplyTo: '',
        MailMode: 'template', // 'template' or 'compose'
        Subject: '',
        TemplateId: '',
        Body: ''
    });

    // 3. SMS
    const [smsAccounts, setSmsAccounts] = useState([]);
    const [smsSenderIds, setSmsSenderIds] = useState([]);
    const [smsTemplates, setSmsTemplates] = useState([]);
    const [smsTab, setSmsTab] = useState('Indian SMS');
    const [smsData, setSmsData] = useState({
        ChooseNumbers: [],
        Account: '',
        SenderId: '',
        TemplateId: '',
        Message: '',
        IsUnicode: false,
        SmsType: 'Compose'
    });

    // 4. Voice
    const [dniNumbers, setDniNumbers] = useState([]);
    const [appFlows, setAppFlows] = useState([]);
    const [leadMobileNos, setLeadMobileNos] = useState([]);
    const [voiceData, setVoiceData] = useState({
        ChooseNumbers: [],
        DnidNo: '',
        AppFlow: '',
        Route: 'Transactional',
        Duration: '30'
    });

    // 5. Note
    const [noteText, setNoteText] = useState('');
    const [documentData, setDocumentData] = useState({ Title: '', File: null, FileName: '' });
    const [showMediaLibrary, setShowMediaLibrary] = useState(false);
    
    const handleMediaSelect = async (media) => {
        setShowMediaLibrary(false);
        if (!media || (!media.url && !media.thumbnail)) return;
        
        const fileUrl = media.url || media.thumbnail;
        const fileName = media.name || media.FileName || "document.pdf";
        
        try {
            const response = await fetch(fileUrl);
            const blob = await response.blob();
            const file = new File([blob], fileName, { type: blob.type || "application/octet-stream" });
            setDocumentData(p => ({ ...p, File: file, FileName: file.name, Title: p.Title || file.name.split('.')[0] }));
        } catch (err) {
            console.error("Error fetching media from library:", err);
            alert("Failed to load selected media from library. " + err.message);
        }
    };

    // 7. Task
    const [salesActivityOptions, setActivityOptions] = useState([]);
    const [collaboratorOptions, setCollaboratorOptions] = useState([]);
    const [ownerOptions, setOwnerOptions] = useState([]);
    const [taskData, setTaskData] = useState({
        Title: `NewTask_${new Date().toISOString().replace(/[-:T.Z]/g, '').slice(0, 14)}`,
        Description: '',
        Remarks: '',
        SalesActivityType: '7',
        DueDate: new Date().toISOString().split('T')[0],
        DueTime: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }),
        Owner: '34594',
        Collaborators: '39768'
    });

    // 8. Appointment
    const [appointmentData, setAppointmentData] = useState({
        Title: '',
        Description: '',
        Remarks: '',
        SalesActivityType: '',
        StartDate: '',
        StartTime: '',
        EndDate: '',
        EndTime: '',
        Where: '',
        Owner: '',
        Collaborators: '',
        Latitude: '',
        Longitude: ''
    });
    const [scriptsLoaded, setScriptsLoaded] = useState(false);
    const whereInputRef = useRef(null);
    const googleAutocompleteRef = useRef(null);

    // 9. Deal
    const [dealPipelines, setDealPipelines] = useState([]);
    const [dealStages, setDealStages] = useState([]);
    const [dealData, setDealData] = useState({
        Amount: '',
        PipelineId: '',
        PipelineStage: '',
        Probability: '',
        DealOwner: '',
        ClosureDate: new Date().toISOString().split('T')[0]
    });

    // 10. Webform
    const [webforms, setWebforms] = useState([]);
    const [selectedWebform, setSelectedWebform] = useState('');
    const showOperationSuccess = (message) => {
        Swal.fire({
            icon: 'success',
            title: 'Success',
            text: message,
            confirmButtonText: 'OK',
            confirmButtonColor: '#28a745'
        });
    };

    useEffect(() => {
        if (isOpen && operationMode === 'addlead') {
            fetchLeadMetadata();
        }
    }, [isOpen, operationMode]);

    const fetchLeadMetadata = async () => {
        try {
            const { parentId, TokenId, userId } = getSession();
            const finalToken = TokenId || TOKEN;
            const payloadOptions = {
                Token: finalToken,
                Message: "",
                LoggedUserId: userId ?? 0,
                MAC_Address: "",
                IP_Address: "102.16.32.189",
                BroadcastName: ""
            };

            const [sourceList, mediumList, campaignList] = await Promise.all([
                bindSourceMediumCampaign({ ...payloadOptions, Details: { Type: 'SourceName', Parentid: Number(parentId || 0) } }),
                bindSourceMediumCampaign({ ...payloadOptions, Details: { Type: 'MediumName', Parentid: Number(parentId || 0) } }),
                bindSourceMediumCampaign({ ...payloadOptions, Details: { Type: 'CampaignName', Parentid: Number(parentId || 0) } })
            ]);

            setSourceOptions(sourceList);
            setMediumOptions(mediumList);
            setCampaignOptions(campaignList);
        } catch (error) {
            console.error('Error fetching lead metadata:', error);
        }
    };

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (sidebarSearchRef.current && !sidebarSearchRef.current.contains(e.target)) {
                setIsDropdownOpen(false);
            }
            if (productRef.current && !productRef.current.contains(e.target)) {
                setIsProductDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const resetForm = () => {
        // Reset Search & Stage 1
        setSearchQuery('');
        setSuggestions([]);
        setSelectedRecord(null);
        setIsDropdownOpen(false);

        // Reset Followup
        setProductSearch('');
        setProductSuggestions([]);
        setSelectedProducts([]);
        setFollowupForm({
            status: followupStatuses[0]?.Value || followupStatuses[0]?.Id || followupStatuses[0]?.Text || '',
            assignedUser: '',
            dueDate: new Date().toISOString().slice(0, 16).replace('T', ' '),
            remarks: '',
            isReAssign: false
        });

        // Reset Lead
        setLeadForm({
            PersonName: '', CompanyName: '', Mobile1: '', Email1: '',
            Source: '', Medium: '', Campaign: '', EnquiryTags: '',
            Remarks: '', ResiAddress: '', OfficeAddress: '', PinCode: '0'
        });

        // Reset others
        setMailData({
            ChooseEmails: [], Account: '', From: '', ReplyTo: '',
            MailMode: 'template', Subject: '', TemplateId: '', Body: ''
        });
        setSmsData({
            ChooseNumbers: [], Account: '', SenderId: '',
            TemplateId: '', Message: '', IsUnicode: false, SmsType: 'Compose'
        });
        setVoiceData({
            ChooseNumbers: [], DnidNo: dniNumbers[0]?.Code || '',
            AppFlow: appFlows[0]?.Id || '', Route: 'Transactional',
            Duration: '30'
        });
        setLeadMobileNos([]);
        setNoteText('');
        setDocumentData({ File: null, FileName: '', Title: '' });
        setTaskData({ Title: '', Description: '', SalesActivityType: '', DueDate: '', DueTime: '', Owner: '34594' });
        setAppointmentData({
            Title: '', Description: '', ActivityType: '', StartDate: '',
            StartTime: '', EndDate: '', EndTime: '', Where: '', Owner: '34594'
        });
        setDealData({
            Amount: '', PipelineId: '', PipelineStage: '',
            Probability: '', DealOwner: '34594', ClosureDate: new Date().toISOString().split('T')[0]
        });
        setSelectedWebform('');
    };

    // Sync external record to internal state when drawer opens
    useEffect(() => {
        if (isOpen) {
            if (externalSelectedRecord) {
                setSelectedRecord(externalSelectedRecord);
            }
            if (externalAssociatedTo) {
                setAssociatedTo(externalAssociatedTo);
            }
        }
    }, [isOpen, externalSelectedRecord, externalAssociatedTo]);

    // Reset when closed
    useEffect(() => {
        if (!isOpen) {
            resetForm();
        }
    }, [isOpen]);

    // Load necessary data when mode changes or record is selected
    useEffect(() => {
        if (selectedRecord && isOpen) {
            fetchModeSpecificData();
        }
    }, [selectedRecord, isOpen, operationMode]);

    useEffect(() => {
        if (isOpen && (operationMode === 'addtask' || operationMode === 'addappointment') && selectedRecord) {
            const now = new Date();
            const day = String(now.getDate()).padStart(2, '0');
            const month = String(now.getMonth() + 1).padStart(2, '0');
            const year = now.getFullYear();
            const time = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }).replace(/:/g, '');
            const autoTitle = operationMode === 'addtask'
                ? `NewTask_${year}${month}${day}${time}`
                : `NewAppointment_${year}${month}${day}${time}`;

            if (operationMode === 'addtask') {
                setTaskData(prev => ({
                    ...prev,
                    Title: autoTitle,
                    DueDate: now.toISOString().split('T')[0],
                    DueTime: now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
                }));
            } else {
                setAppointmentData(prev => ({
                    ...prev,
                    Title: autoTitle,
                    StartDate: now.toISOString().split('T')[0],
                    StartTime: now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }),
                    EndDate: now.toISOString().split('T')[0],
                    EndTime: now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
                }));
            }
        }
    }, [isOpen, operationMode, selectedRecord]);

    useEffect(() => {
        if (operationMode === 'addappointment') {
            const scriptId = 'google-maps-script';
            const isGoogleMapsReady = () => window.google && window.google.maps && window.google.maps.places;

            if (isGoogleMapsReady()) {
                setScriptsLoaded(true);
                return;
            }

            if (!document.getElementById(scriptId)) {
                const script = document.createElement('script');
                script.id = scriptId;
                script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyAO-dLMQIXYLvJeig0dbjeEqytvPg6EMd8&libraries=places&loading=async&callback=initGoogleMapsCallback`;
                script.async = true;
                script.defer = true;
                window.initGoogleMapsCallback = () => { setScriptsLoaded(true); };
                script.onerror = () => { console.error("Google Maps script failed to load"); };
                document.body.appendChild(script);
            } else {
                const checkInterval = setInterval(() => {
                    if (isGoogleMapsReady()) {
                        setScriptsLoaded(true);
                        clearInterval(checkInterval);
                    }
                }, 100);
                setTimeout(() => clearInterval(checkInterval), 10000);
            }
        }
    }, [operationMode]);

    useEffect(() => {
        let autocompleteListener = null;
        if (scriptsLoaded && isOpen && operationMode === 'addappointment') {
            const initAutocomplete = () => {
                if (!whereInputRef.current || !window.google?.maps?.places) return false;
                googleAutocompleteRef.current = new window.google.maps.places.Autocomplete(whereInputRef.current, {
                    types: ['geocode', 'establishment'],
                });
                autocompleteListener = googleAutocompleteRef.current.addListener('place_changed', () => {
                    const place = googleAutocompleteRef.current.getPlace();
                    const address = place.formatted_address || place.name;
                    setAppointmentData(prev => ({
                        ...prev,
                        Where: address,
                        Latitude: place.geometry?.location?.lat() || '',
                        Longitude: place.geometry?.location?.lng() || ''
                    }));
                });
                return true;
            };
            if (!initAutocomplete()) {
                const pollInterval = setInterval(() => { if (initAutocomplete()) clearInterval(pollInterval); }, 100);
                setTimeout(() => clearInterval(pollInterval), 5000);
            }
        }
        return () => {
            if (autocompleteListener && window.google?.maps?.event) {
                window.google.maps.event.removeListener(autocompleteListener);
            }
        };
    }, [scriptsLoaded, isOpen, operationMode]);

    useEffect(() => {
        if (isOpen && operationMode === 'addlead') {
            fetchLeadMetadata();
        }
    }, [isOpen, operationMode]);

    const fetchModeSpecificData = async () => {
        setIsLoadingInitialData(true);
        try {
            const uid = localStorage.getItem('userId') || '34594';
            const leadId = selectedRecord?.Id || selectedRecord?.leadId || selectedRecord?.enquiryId || "0";

            switch (operationMode) {
                case 'followup':
                    await fetchFollowupInitialData(uid);
                    break;
                case 'mail':
                    await fetchMailInitialData(uid, leadId);
                    break;
                case 'sms':
                    await fetchSmsInitialData(uid, leadId);
                    break;
                case 'voice':
                    await fetchVoiceInitialData(uid, leadId);
                    break;
                case 'addtask':
                case 'addappointment':
                    await fetchTaskInitialData(uid);
                    break;
                case 'adddeal':
                    await fetchDealInitialData(uid);
                    await fetchTaskInitialData(uid);
                    break;
                case 'fillWebform':
                    await fetchWebformsList(uid);
                    break;
            }
        } catch (error) {
            console.error('Error fetching mode data:', error);
        } finally {
            setIsLoadingInitialData(false);
        }
    };

    // --- FETCHERS ---

    const fetchFollowupInitialData = async (uid) => {
        try {
            const statusPromise = fetch(apiFollowup_List, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
                body: JSON.stringify({ Token: TOKEN, Details: { ParentId: Number(uid) } })
            }).then(r => r.json());

            const hierarchyPromise = fetch(Get_User_Hierarchy_List, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
                body: JSON.stringify({ Token: TOKEN, Details: { UserId: parseInt(uid) } })
            }).then(r => r.json());

            const [statusData, hierarchyData] = await Promise.all([statusPromise, hierarchyPromise]);

            if (statusData) {
                const rawStatus = statusData.Details || statusData.d || statusData.data || statusData;
                const parsedStatus = typeof rawStatus === 'string' ? JSON.parse(rawStatus) : rawStatus;
                if (Array.isArray(parsedStatus)) {
                    setFollowupStatuses(parsedStatus);
                }
            }
            if (hierarchyData) {
                let results = [];
                const raw = hierarchyData.Details?.data || hierarchyData.Details || hierarchyData.d || hierarchyData;
                if (Array.isArray(raw)) {
                    results = raw;
                } else if (typeof raw === 'string') {
                    try {
                        const parsed = JSON.parse(raw);
                        results = parsed?.Table || parsed || [];
                    } catch(e){}
                } else if (raw?.Table) {
                    results = raw.Table;
                } else {
                    results = raw || [];
                }
                setUserHierarchy(Array.isArray(results) ? results : []);
            }
        } catch (e) {
            console.error('Followup Init Error:', e);
        }
    };

    const handleProductSearch = (val) => {
        setProductSearch(val);
        if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
        if (val.length < 2) {
            setProductSuggestions([]);
            setIsProductDropdownOpen(false);
            return;
        }

        setIsProductDropdownOpen(true); // Open immediately to show "Searching..."
        searchTimerRef.current = setTimeout(async () => {
            setIsProductLoading(true);
            try {
                const uid = localStorage.getItem('userId') || '34594';
                const resp = await fetch(Product_Suggestion_API, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        Token: "-2295521862261168",
                        Details: { UserId: parseInt(uid), Term: val }
                    })
                });
                const data = await resp.json();
                let results = [];
                const raw = data?.Details || data?.d || data;
                if (Array.isArray(raw)) {
                    results = raw;
                } else if (typeof raw === 'string') {
                    const parsed = JSON.parse(raw);
                    results = parsed?.Table || parsed || [];
                } else {
                    results = (raw?.Table || raw) || [];
                }
                setProductSuggestions(Array.isArray(results) ? results : []);
            } catch (e) {
                console.error('Product Search Error:', e);
                setIsProductDropdownOpen(false);
            } finally {
                setIsProductLoading(false);
            }
        }, 300);
    };

    const addProduct = (p) => {
        if (!selectedProducts.find(x => (x.ProductId || x.Id) === (p.ProductId || p.Id))) {
            const newProducts = [...selectedProducts, p];
            setSelectedProducts(newProducts);
            setFollowupForm(prev => ({ ...prev, Products: newProducts.map(x => x.ProductId || x.Id).join(',') }));
        }
        setProductSearch('');
        setProductSuggestions([]);
        setIsProductDropdownOpen(false);
    };

    const fetchMailInitialData = async (uid, leadId) => {
        try {
            // Fetch Accounts
            const accResponse = await fetch(`${API_SERVICES}/${API_ENDPOINTS.COMMON.GET_MAIL_SMS_DLT_ACCOUNT}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ Token: TOKEN, Details: { ParentId: parseInt(localStorage.getItem('parentId')) || parseInt(uid), isSMS: 0, isMail: 1 } })
            });
            const accData = await accResponse.json();
            if (accData?.Details) setMailAccounts(typeof accData.Details === 'string' ? JSON.parse(accData.Details) : accData.Details);

            // Fetch Record Details for Emails
            if (leadId && leadId !== "0") {
                let leadObj = {};
                if (associatedTo === 'Enquiry') {
                    const enqRes = await fetch(Get_Enquiry_Data, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            Token: TOKEN,
                            Details: {
                                txt_search: "", SearchText: "", TextSearch: "",
                                FilterText: `EnquiryId=${leadId}`,
                                parent_id: parseInt(localStorage.getItem('parentId')) || parseInt(uid), ParentId: parseInt(localStorage.getItem('parentId')) || parseInt(uid),
                                user_id: parseInt(uid), UserId: parseInt(uid),
                                filter_text: `EnquiryId=${leadId}`,
                                order_str: "", srno: "1", enno: "20", ue_query: ""
                            }
                        })
                    });
                    const enqDataRes = await enqRes.json();
                    const enqList = (enqDataRes?.Details || []);
                    if (enqList.length > 0) leadObj = enqList[0];
                } else {
                    const uid = localStorage.getItem('userId') || '34594';
                    const detailsStr = JSON.stringify({
                        draw: 1,
                        StartNo: 0,
                        EndNo: 1,
                        UserId: parseInt(uid, 10),
                        ParentId: parseInt(uid, 10),
                        FilterText: `ID=${leadId}`
                    });
                    const leadRes = await fetch(LeadDetails, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            Token: "-2295521862261168",
                            Details: detailsStr
                        })
                    });
                    const leadDataRes = await leadRes.json();
                    let parsedDetails = leadDataRes?.Details;
                    if (typeof parsedDetails === 'string') {
                        try { parsedDetails = JSON.parse(parsedDetails); } catch(e) {}
                    }
                    leadObj = parsedDetails?.data?.[0] || parsedDetails || leadDataRes?.d || leadDataRes || {};
                }
                const emails = new Set();
                [leadObj.email, leadObj.Email, leadObj.Emails, leadObj.EmailID, leadObj.Email1, leadObj.Email2, leadObj.Email3, leadObj.PersonalEmail, leadObj.CompanyEmail]
                    .filter(Boolean)
                    .forEach(e => emails.add(e));
                const uniqueEmails = Array.from(emails);
                setLeadEmails(uniqueEmails);
                if (uniqueEmails.length > 0) {
                    setMailData(p => ({ ...p, ChooseEmails: [uniqueEmails[0]] }));
                }
            }

            // Fetch From Emails - USING NEW SPECIFIED PAYLOAD
            const fromResponse = await fetch(FROM_MAIL_IDS, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    Status: "", Message: "", Token: TOKEN,
                    Details: { UserId: parseInt(uid) }
                })
            });
            const fromData = await fromResponse.json();
            const fromRaw = fromData?.Details || fromData?.d || fromData;
            const parsedFrom = typeof fromRaw === 'string' ? JSON.parse(fromRaw) : fromRaw;
            setFromMailOptions(Array.isArray(parsedFrom) ? parsedFrom : []);

            // Fetch Templates
            const tempResponse = await fetch(`${API_BASE_SERVICES}/${API_ENDPOINTS.COMMON.NEW_GET_TEMPLATE_FOR_MAIL}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ Token: TOKEN, Details: { ParentId: parseInt(localStorage.getItem('parentId')) || parseInt(uid), isSMS: 0, isMail: 1 } })
            });
            const tempData = await tempResponse.json();
            if (tempData?.Details) setMailTemplates(typeof tempData.Details === 'string' ? JSON.parse(tempData.Details) : tempData.Details);
        } catch (e) { console.error('Mail Init Error:', e); }
    };

    const fetchSmsInitialData = async (uid, leadId) => {
        try {
            // Fetch SMS Accounts
            const accResponse = await fetch(`${API_SERVICES}/${API_ENDPOINTS.COMMON.GET_MAIL_SMS_DLT_ACCOUNT}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ Token: TOKEN, Details: { ParentId: parseInt(localStorage.getItem('parentId')) || parseInt(uid), isSMS: 1, isMail: 0 } })
            });
            const accData = await accResponse.json();
            if (accData?.Details) setSmsAccounts(typeof accData.Details === 'string' ? JSON.parse(accData.Details) : accData.Details);

            // Fetch Sender IDs
            const senderResponse = await fetch(`${API_SERVICES}/${API_ENDPOINTS.APPOINTMENTS.GET_SENDER_IDFOR_SMS}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ UserID: parseInt(uid) })
            });
            const senderData = await senderResponse.json();
            if (senderData?.d) setSmsSenderIds(senderData.d);
            // 2. Fetch Record Mobile Numbers
            if (leadId && leadId !== "0") {
                let leadObj = {};
                if (associatedTo === 'Enquiry') {
                    const enqRes = await fetch(Get_Enquiry_Data, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            Token: TOKEN,
                            Details: {
                                txt_search: "", SearchText: "", TextSearch: "",
                                FilterText: `EnquiryId=${leadId}`,
                                parent_id: parseInt(uid), ParentId: parseInt(uid),
                                user_id: parseInt(uid), UserId: parseInt(uid),
                                filter_text: `EnquiryId=${leadId}`,
                                order_str: "", srno: "1", enno: "20", ue_query: ""
                            }
                        })
                    });
                    const enqDataRes = await enqRes.json();
                    const enqList = (enqDataRes?.Details || []);
                    if (enqList.length > 0) leadObj = enqList[0];
                } else {
                    const uid = localStorage.getItem('userId') || '34594';
                    const detailsStr = JSON.stringify({
                        draw: 1,
                        StartNo: 0,
                        EndNo: 1,
                        UserId: parseInt(uid, 10),
                        ParentId: parseInt(uid, 10),
                        FilterText: `ID=${leadId}`
                    });
                    const leadRes = await fetch(LeadDetails, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            Token: "-2295521862261168",
                            Details: detailsStr
                        })
                    });
                    const leadDataRes = await leadRes.json();
                    let parsedDetails = leadDataRes?.Details;
                    if (typeof parsedDetails === 'string') {
                        try { parsedDetails = JSON.parse(parsedDetails); } catch(e) {}
                    }
                    leadObj = parsedDetails?.data?.[0] || parsedDetails || leadDataRes?.d || leadDataRes || {};
                }
                const numbers = new Set();
                [leadObj.Mobile, leadObj.MobileNo, leadObj.mobile, leadObj.Mobile1, leadObj.Mobile2, leadObj.Phone1, leadObj.Landline, leadObj.Phones, leadObj.phones, leadObj.Phone, leadObj.phone]
                    .filter(Boolean)
                    .forEach(n => {
                        const cleaned = n.toString().trim();
                        if (cleaned) numbers.add(cleaned);
                    });
                const uniqueNumbers = Array.from(numbers);
                setLeadMobileNos(uniqueNumbers);
                if (uniqueNumbers.length > 0) {
                    setSmsData(p => ({ ...p, ChooseNumbers: [uniqueNumbers[0]] }));
                }
            }
        } catch (e) { console.error('SMS Init Error:', e); }
    };

    const fetchVoiceInitialData = async (uid, leadId) => {
        try {
            // 1. Fetch Record Mobile Numbers
            if (leadId && leadId !== "0") {
                let leadObj = {};
                if (associatedTo === 'Enquiry') {
                    const enqRes = await fetch(Get_Enquiry_Data, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            Token: TOKEN,
                            Details: {
                                txt_search: "", SearchText: "", TextSearch: "",
                                FilterText: `EnquiryId=${leadId}`,
                                parent_id: parseInt(uid), ParentId: parseInt(uid),
                                user_id: parseInt(uid), UserId: parseInt(uid),
                                filter_text: `EnquiryId=${leadId}`,
                                order_str: "", srno: "1", enno: "20", ue_query: ""
                            }
                        })
                    });
                    const enqDataRes = await enqRes.json();
                    const enqList = (enqDataRes?.Details || []);
                    if (enqList.length > 0) leadObj = enqList[0];
                } else {
                    const uid = localStorage.getItem('userId') || '34594';
                    const detailsStr = JSON.stringify({
                        draw: 1,
                        StartNo: 0,
                        EndNo: 1,
                        UserId: parseInt(uid, 10),
                        ParentId: parseInt(uid, 10),
                        FilterText: `ID=${leadId}`
                    });
                    const leadRes = await fetch(LeadDetails, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            Token: "-2295521862261168",
                            Details: detailsStr
                        })
                    });
                    const leadDataRes = await leadRes.json();
                    let parsedDetails = leadDataRes?.Details;
                    if (typeof parsedDetails === 'string') {
                        try { parsedDetails = JSON.parse(parsedDetails); } catch(e) {}
                    }
                    leadObj = parsedDetails?.data?.[0] || parsedDetails || leadDataRes?.d || leadDataRes || {};
                }
                const numbers = new Set();
                [leadObj.Mobile, leadObj.MobileNo, leadObj.mobile, leadObj.Mobile1, leadObj.Mobile2, leadObj.Phone1, leadObj.Landline, leadObj.Phones, leadObj.phones, leadObj.Phone, leadObj.phone]
                    .filter(Boolean)
                    .forEach(n => {
                        // Handle phone numbers that may contain country codes like "+971 42734180"
                        const cleaned = n.toString().trim();
                        if (cleaned) numbers.add(cleaned);
                    });
                const uniqueNumbers = Array.from(numbers);
                setLeadMobileNos(uniqueNumbers);
                if (uniqueNumbers.length > 0) {
                    setVoiceData(p => ({ ...p, ChooseNumbers: [uniqueNumbers[0]] }));
                }
            }

            // Universal Fetch Helper
            const safeParse = (data) => {
                let p = [];
                if (data?.d && typeof data.d === 'string') {
                    const parsed = JSON.parse(data.d);
                    p = parsed?.Table || parsed || [];
                } else if (data?.Details) {
                    const parsed = typeof data.Details === 'string' ? JSON.parse(data.Details) : data.Details;
                    p = parsed?.Table || parsed || [];
                } else if (Array.isArray(data)) {
                    p = data;
                }
                return p;
            };

            // 2. Fetch Caller IDs (Try general fetch first as it often returns everything)
            const generalRes = await fetch(`${API_BASE_SERVICES}/${API_ENDPOINTS.COMMON.NEW_SEND_VOICE}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ Status: "", Message: "", Token: TOKEN, Details: { ParentId: parseInt(uid) } })
            });
            const generalData = await generalRes.json();
            const generalList = safeParse(generalData);

            // If general call has Table1, it might have AppFlows too
            let combinedData = null;
            if (generalData?.Details) {
                combinedData = typeof generalData.Details === 'string' ? JSON.parse(generalData.Details) : generalData.Details;
            } else if (generalData?.d) {
                combinedData = JSON.parse(generalData.d);
            }

            if (combinedData?.Table && combinedData.Table.length > 0) {
                setDniNumbers(combinedData.Table);
                setVoiceData(p => ({ ...p, DnidNo: combinedData.Table[0].Code || combinedData.Table[0].DnidNo }));
            } else if (generalList.length > 0) {
                setDniNumbers(generalList);
                setVoiceData(p => ({ ...p, DnidNo: generalList[0].Code || generalList[0].DnidNo }));
            }

            if (combinedData?.Table1 && combinedData.Table1.length > 0) {
                setAppFlows(combinedData.Table1);
                setVoiceData(p => ({ ...p, AppFlow: combinedData.Table1[0].Id || combinedData.Table1[0].AppId }));
            }

            // 3. Specifically if AppFlow is still empty or Opt:2 is preferred for richness
            const appRes = await fetch(`${API_BASE_SERVICES}/${API_ENDPOINTS.COMMON.NEW_SEND_VOICE}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ Status: "", Message: "", Token: TOKEN, Details: { ParentId: parseInt(uid), AppId: 0, Opt: 2 } })
            });
            const appData = await appRes.json();
            const appList = safeParse(appData);
            if (appList.length > 0) {
                setAppFlows(appList);
                setVoiceData(p => ({ ...p, AppFlow: appList[0].Id || appList[0].AppId }));
            }

        } catch (e) { console.error('Voice Init Error:', e); }
    };

    const fetchTaskInitialData = async (uid) => {
        try {
            // 1. Sales Activity Types
            const actResp = await fetch(`${API_BASE_SERVICES}/${API_ENDPOINTS.TASKS.GET_SALES_ACTIVITY_TYPE}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
                body: JSON.stringify({ Token: "-2295521862261168", Details: { UserId: parseInt(uid, 10) } })
            });
            const actData = await actResp.json();
            const actList = actData?.d || actData?.Details || actData;
            if (Array.isArray(actList)) {
                setActivityOptions(actList);
            } else if (typeof actList === 'string') {
                try {
                    const parsed = JSON.parse(actList);
                    setActivityOptions(parsed?.Table || parsed || []);
                } catch (e) { }
            }

            // 2. Owners and Collaborators using GetUserListByUser_Id
            const usersResp = await fetch(`${API_BASE_SERVICES}/${API_ENDPOINTS.APPOINTMENTS.GET_USERLIST_PHYSICALAPP_VIEW}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
                body: JSON.stringify({ Token: "-2295521862261168", Details: { UserId: parseInt(uid, 10) } })
            });
            const usersData = await usersResp.json();
            const rawUsers = usersData.Details?.data || usersData.Details || usersData.d || usersData;
            
            let finalUsers = [];
            if (Array.isArray(rawUsers)) {
                finalUsers = rawUsers;
            } else if (typeof rawUsers === 'string') {
                try {
                    const parsed = JSON.parse(rawUsers);
                    finalUsers = parsed?.Table || (Array.isArray(parsed) ? parsed : []);
                } catch (e) { }
            } else if (rawUsers?.Table) {
                finalUsers = rawUsers.Table;
            }

            const mappedUsers = finalUsers.map(u => ({
                id: (u.UserId || u.Id || u.ID || u.Code || u.Item1 || u.Value || '').toString(),
                name: u.UserName || u.FirstName || u.Name || u.Text || u.Item2 || u.Label || ''
            }));

            setCollaboratorOptions(mappedUsers);
            setOwnerOptions(mappedUsers);

            // 3. Fallback hierarchy if needed (optional, keeping it as backup)
            const hierResp = await fetch(Get_User_Hierarchy_List, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
                body: JSON.stringify({ Token: TOKEN, Details: { UserId: parseInt(uid) } })
            });
            const hierData = await hierResp.json();
            const rawHier = hierData.Details?.data || hierData.Details || hierData.d || hierData;
            let results = [];
            if (Array.isArray(rawHier)) { results = rawHier; }
            else if (typeof rawHier === 'string') { try { const p = JSON.parse(rawHier); results = p?.Table || p || []; } catch(e){} }
            else if (rawHier?.Table) { results = rawHier.Table; }
            else { results = rawHier || []; }
            if (Array.isArray(results)) {
                const mappedHierarchy = results.map(u => ({
                    id: (u.UserId || u.ID || u.Id || u.Code || '').toString(),
                    name: u.UserName || u.Name || u.FirstName || u.Text || ''
                }));
                setUserHierarchy(mappedHierarchy);
                // If owners list is empty, use hierarchy
                if (mappedUsers.length === 0) setOwnerOptions(mappedHierarchy);
            }
        } catch (e) { console.error('Task Init Error:', e); }
    };

    const fetchSalesOwners = async () => {
        try {
            const uid = localStorage.getItem('userId') || '34594';
            const usersResp = await fetch(`${API_BASE_SERVICES}/${API_ENDPOINTS.APPOINTMENTS.GET_USERLIST_PHYSICALAPP_VIEW}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
                body: JSON.stringify({ Token: "-2295521862261168", Details: { UserId: parseInt(uid, 10) } })
            });
            const usersData = await usersResp.json();
            const rawUsers = usersData.Details?.data || usersData.Details || usersData.d || usersData;
            
            let finalUsers = [];
            if (Array.isArray(rawUsers)) {
                finalUsers = rawUsers;
            } else if (typeof rawUsers === 'string') {
                try {
                    const parsed = JSON.parse(rawUsers);
                    finalUsers = parsed?.Table || (Array.isArray(parsed) ? parsed : []);
                } catch (e) { }
            } else if (rawUsers?.Table) {
                finalUsers = rawUsers.Table;
            }

            const mappedUsers = finalUsers.map(u => ({
                id: (u.UserId || u.Id || u.ID || u.Code || u.Item1 || u.Value || '').toString(),
                name: u.UserName || u.FirstName || u.Name || u.Text || u.Item2 || u.Label || ''
            }));

            setOwnerOptions(mappedUsers);
            if (mappedUsers.length > 0 && !dealData.DealOwner) {
                setDealData(p => ({ ...p, DealOwner: mappedUsers[0].id }));
            }
        } catch (e) { console.error('Sales Owners Error:', e); }
    };

    const fetchDealInitialData = async () => {
        try {
            // Consistent with PipelineDealActionsOperation.jsx
            const pipelines = [
                { id: "152", name: "solar" }, { id: "536", name: "Study" },
                { id: "649", name: "Sales Pipeline" }, { id: "851", name: "Stock Broking" },
                { id: "944", name: "ghgfhfg" }, { id: "955", name: "pipeline test" },
                { id: "974", name: "Loundhery" }, { id: "999", name: "newpipeline" },
                { id: "1144", name: "Skin patient" }
            ];
            setDealPipelines(pipelines);
            if (pipelines.length > 0) {
                setDealData(p => ({ ...p, PipelineId: pipelines[0].id }));
                fetchDealStages(pipelines[0].id);
            }
            fetchSalesOwners();
        } catch (e) { console.error('Deal Init Error:', e); }
    };

    const fetchDealStages = async (pid) => {
        if (!pid) return;
        try {
            const resp = await fetch(`${API_BASE_SERVICES}/${API_ENDPOINTS.COMMON.PIPELINE_STAGE_BASED_ON_DEALPIPELINE}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ Token: "-2295521862261168", Details: Number(pid) })
            });
            const result = await resp.json();
            let dataRaw = result.Details || result.d || result;

            // Robust parsing for stringified data
            if (typeof dataRaw === 'string') {
                try { dataRaw = JSON.parse(dataRaw); } catch (e) { console.error("Parse Error:", e); }
            }

            const stages = (Array.isArray(dataRaw) ? dataRaw : (dataRaw?.Table || dataRaw?.Details || [])).map(s => ({
                id: (s.StageID || s.Id || s.Code || '').toString(),
                name: s.StageName || s.Name || s.Text || ''
            }));

            setDealStages(stages);
            if (stages.length > 0) {
                setDealData(p => ({ ...p, PipelineStage: stages[0].id }));
            }
        } catch (e) { console.error('Deal Stages Error:', e); }
    };

    const fetchWebformsList = async (uid) => {
        try {
            const resp = await fetch(`${API_BASE_SERVICES}/${API_ENDPOINTS.COMMON.GET_WEBFORM_LIST_BYUSERID}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ Token: "-2295521862261168", Details: { UserId: parseInt(uid), OffsetValue: 0, PagingSize: 100 } })
            });
            const data = await resp.json();
            if (data?.Details) setWebforms(typeof data.Details === 'string' ? JSON.parse(data.Details) : data.Details);
        } catch (e) { console.error('Webform List Error:', e); }
    };

    // --- SHARED HANDLERS ---

    const handleSearchChange = (val) => {
        setSearchQuery(val);
        if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
        if (val.length < 1) {
            setSuggestions([]);
            setIsDropdownOpen(false);
            return;
        }
        searchTimerRef.current = setTimeout(async () => {
            setIsLoadingSuggestions(true);
            try {
                const uid2 = localStorage.getItem('userId') || '34594';

                let endpoint = Related_TO_Suggestion;
                let payload = {
                    Token: '-2295521862261168',
                    Details: {
                        TaskType: associatedTo === 'Enquiry' ? 'Enquiry' : 'Lead',
                        FilterText: val,
                        UserId: parseInt(uid2)
                    }
                };

                const response = await fetch(endpoint, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
                    body: JSON.stringify(payload)
                });
                const data = await response.json();

                // Flexible parsing: Handle direct array, data.d (WCF), or data.Details
                let results = [];
                if (Array.isArray(data)) {
                    results = data;
                } else if (data?.d) {
                    if (typeof data.d === 'string') {
                        const parsed = JSON.parse(data.d);
                        results = parsed?.Table || (Array.isArray(parsed) ? parsed : []);
                    } else if (Array.isArray(data.d)) {
                        results = data.d;
                    }
                } else if (data?.Details) {
                    results = typeof data.Details === 'string' ? (JSON.parse(data.Details)?.Table || JSON.parse(data.Details) || []) : (data.Details?.Table || data.Details || []);
                }

                setSuggestions(results);
                setIsDropdownOpen(results.length > 0);
            } catch (error) {
                console.error('Record Suggestion Error:', error);
            } finally {
                setIsLoadingSuggestions(false);
            }
        }, 350);
    };

    const handleSelect = (record) => {
        setSelectedRecord(record);
        setSearchQuery('');
        setSuggestions([]);
        setIsDropdownOpen(false);
    };

    const handleMailChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (type === 'checkbox') {
            const current = mailData[name] || [];
            const next = checked ? [...current, value] : current.filter(x => x !== value);
            setMailData(p => ({ ...p, [name]: next }));
        } else {
            setMailData(p => ({ ...p, [name]: value }));
        }
    };

    const handleSmsChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (type === 'checkbox') {
            const current = smsData[name] || [];
            const next = checked ? [...current, value] : current.filter(x => x !== value);
            setSmsData(p => ({ ...p, [name]: next }));
        } else {
            setSmsData(p => ({ ...p, [name]: value }));
        }
    };

    const handleVoiceChange = (e) => {
        const { name, value } = e.target;
        setVoiceData(p => ({ ...p, [name]: value }));
    };

    const handleSubmit = async () => {
        if (operationMode === 'addlead') {
            await handleSaveLead();
            return;
        }
        if (!selectedRecord) { alert("Please select a record first"); return; }
        setIsSubmitting(true);
        try {
            switch (operationMode) {
                case 'followup': await handleSaveFollowup(); break;
                case 'mail': await handleSendMail(); break;
                case 'sms': await handleSendSms(); break;
                case 'voice': await handleSendVoice(); break;
                case 'note': await handleSaveNote(); break;
                case 'upload': await handleSaveUpload(); break;
                case 'addtask': await handleSaveTask(); break;
                case 'addappointment': await handleSaveAppointment(); break;
                case 'adddeal': await handleSaveDeal(); break;
                case 'fillWebform': await handleSaveWebform(); break;
            }
        } catch (e) {
            console.error('Submit Error:', e);
            alert('Operation failed');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSaveFollowup = async () => {
        if (!followupForm.status || !followupForm.remarks) {
            Swal.fire({ icon: 'warning', title: 'Missing Information', text: 'Please fill Status and Remarks' });
            return;
        }

        const uid = localStorage.getItem('userId') || '34594';
        const d = new Date(followupForm.dueDate);
        const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        const fmtD = `${String(d.getDate()).padStart(2, '0')}-${months[d.getMonth()]}-${d.getFullYear()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:00`;

        const payload = {
            Token: "-2295521862261168",
            Details: {
                objLeadFollowup_BO: {
                    LeadId: parseInt(selectedRecord?.Id || selectedRecord?.LeadID || selectedRecord?.EnquiryID || selectedRecord?.enquiryId || "0"),
                    AssignedTo: parseInt(followupForm.assignedUser || uid),
                    ParentID: parseInt(uid),
                    CreatedBy: parseInt(uid),
                    FollowUpStatus: parseInt(String(followupForm.status).split(',')[0]) || 0,
                    FupValue: String(followupForm.status).includes(',') ? String(followupForm.status) : `${followupForm.status},0,0`,
                    NextStatusDate: fmtD,
                    Remarks: followupForm.remarks,
                    Products: selectedProducts.length > 0 ? selectedProducts.map(p => p.ProductName || p.Name || p.Text || "").join(',') : (followupForm.Products || ""),
                    AmountPaid: 0,
                    IsReAssign: !!followupForm.isReAssign,
                    NotifyBySMS: false,
                    NotifyByEmail: false,
                    SmsScheduelDateTime: fmtD,
                    EmailScheduelDateTime: fmtD
                },
                strDLTTemplateID: "",
                strUserID: uid,
                strTimeZone: "India Standard Time",
                strParentID: uid
            }
        };

        try {
            const resp = await fetch(`${LOCAL_BASE_URL}${API_ENDPOINTS.COMMON.ADD_FOLLOW_UP_DLT}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                },
                body: JSON.stringify(payload)
            });

            if (resp.ok) {
                showOperationSuccess('Follow-up added successfully.');
                onClose();
            } else {
                const errorData = await resp.text();
                console.error("Follow-up submission failed:", errorData);
                alert('Operation failed');
            }
        } catch (error) {
            console.error("Error in handleSaveFollowup:", error);
            alert('Operation failed');
        }
    };

    const handleSendMail = async () => {
        if (mailData.ChooseEmails.length === 0) { alert("Select email"); return; }
        const uid = localStorage.getItem('userId') || '34594';
        const userName = localStorage.getItem('userName') || 'CRM user';
        const payload = {
            Token: "-2295521862261168",
            Details: {
                objSendMail: {
                    UserId: uid,
                    EnquiryLeadId: (selectedRecord.EnquiryID || selectedRecord.LeadID || selectedRecord.Id || selectedRecord.text || "0").toString(),
                    Account: mailData.Account || "0",
                    SenderName: userName,
                    EmailType: mailData.MailMode === 'template' ? "Template" : "Compose",
                    Email: mailData.ChooseEmails.join(','),
                    Subject: mailData.Subject,
                    Message: mailData.Body,
                    Template: mailData.TemplateId || "0",
                    EntityType: selectedRecord.EnquiryID ? "Enquiry" : "Lead",
                    ReplyTo: mailData.ReplyTo || mailData.From || "",
                    FromMailID: mailData.From || mailData.ReplyTo || ""
                }
            }
        };
        const resp = await fetch(`${API_SERVICES}${API_ENDPOINTS.COMMON.SEND_MAIL_NEW}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify(payload)
        });
        if (resp.ok) { showOperationSuccess('Email sent successfully.'); onClose(); }
    };

    const handleSendSms = async () => {
        if (smsData.ChooseNumbers.length === 0) { Swal.fire({ icon: 'warning', title: 'Missing Info', text: 'Please select at least one number' }); return; }
        if (!smsData.SenderId) { Swal.fire({ icon: 'warning', title: 'Configuration Error', text: 'Sender id not configured' }); return; }
        const uid = localStorage.getItem('userId') || '34594';
        const payload = {
            Token: "-2295521862261168",
            Details: {
                LeadId: parseInt(selectedRecord.EnquiryID || selectedRecord.LeadID || selectedRecord.Id || selectedRecord.text),
                AccountId: Number(uid), SenderId: smsData.SenderId, TemplateId: smsData.TemplateId.toString(),
                Message: smsData.Message, IsUnicode: !!smsData.IsUnicode,
                MobileNos: smsData.ChooseNumbers.map(n => n.includes('91') ? n : `91 ${n}`), UserId: Number(uid)
            }
        };
        const resp = await fetch(`${API_SERVICES}/${API_ENDPOINTS.COMMON.SEND_SMS}`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload)
        });
        if (resp.ok) {
            showOperationSuccess('SMS sent successfully.');
            onClose();
        }
    };

    const handleSendVoice = async () => {
        if (voiceData.ChooseNumbers.length === 0) { alert("Please select a mobile number"); return; }
        if (!voiceData.DnidNo) { alert("Please select a Caller ID"); return; }
        if (!voiceData.AppFlow) { alert("Please select an App Flow"); return; }

        const uid = localStorage.getItem('userId') || '34594';
        const leadId = parseInt(selectedRecord.EnquiryID || selectedRecord.LeadID || selectedRecord.Id || selectedRecord.text || "0");
        const mobileNo = voiceData.ChooseNumbers[0]?.replace(/\D/g, '') || '';
        const routeMap = { 'Transactional': '2', 'Promotional': '1' };

        const payload = {
            Token: TOKEN || "-2295521862261168",
            Details: {
                ParentId: parseInt(uid),
                SendVoice: {
                    MobileNo: mobileNo,
                    MobileNo1: voiceData.ChooseNumbers[1]?.replace(/\D/g, '') || '',
                    MobileNo2: voiceData.ChooseNumbers[2]?.replace(/\D/g, '') || '',
                    Route: routeMap[voiceData.Route] || '2',
                    AppFlow: voiceData.AppFlow.toString(),
                    DnidNo: voiceData.DnidNo.toString(),
                    Duration: voiceData.Duration || '30',
                    Voice_Source: '',
                    EntityName: 'Lead',
                    EntityId: leadId.toString()
                }
            }
        };

        const resp = await fetch(`${LOCAL_BASE_URL}${API_ENDPOINTS.COMMON.SEND_VOICE_NEW}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });

        if (resp.ok) {
            showOperationSuccess('Voice sent successfully.');
            onClose();
        } else {
            alert('Failed to send voice enquiry');
        }
    };

    const handleSaveNote = async () => {
        if (!noteText.trim()) { alert("Enter note"); return; }
        const uid = localStorage.getItem('userId') || '34594';
        const payload = {
            Token: "-2295521862261168",
            Details: {
                LeadId: (selectedRecord.EnquiryID || selectedRecord.LeadID || selectedRecord.Id || selectedRecord.text).toString(),
                Notes: noteText, UserId: Number(uid)
            }
        };
        const resp = await fetch(`${API_SERVICES}/${API_ENDPOINTS.COMMON.ADD_NOTES}`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload)
        });
        if (resp.ok) { showOperationSuccess('Note added successfully.'); onClose(); }
    };

    const handleSaveUpload = async () => {
        if (!documentData.File) { alert("Select file"); return; }
        setIsSubmitting(true);
        try {
            const uid = localStorage.getItem('userId') || '34594';
            const leadIdStr = (selectedRecord.EnquiryID || selectedRecord.LeadID || selectedRecord.Id || selectedRecord.text).toString();
            
            const details = {
                Status: "",
                Message: "",
                Token: TOKEN,
                fileUpload: {
                    ParentId: parseInt(uid, 10),
                    CreatedBy: parseInt(uid, 10),
                    LeadId: leadIdStr
                }
            };
            
            const formData = new FormData();
            formData.append("details", JSON.stringify(details));
            formData.append("file", documentData.File);
            
            const Upload_Document = `${SERVICE_AZURE_URL}${API_ENDPOINTS.COMMON.UPLOAD_DOCUMENT}`;
            const resp = await fetch(Upload_Document, {
                method: 'POST',
                body: formData
            });
            
            if (resp.ok) {
                const result = await resp.json();
                const isSuccess = result.Status === 1 || result.status === 1 || (result.Message && result.Message.toLowerCase().includes("success"));
                if (isSuccess) {
                    showOperationSuccess('Document uploaded successfully.');
                    onClose();
                } else {
                    alert(result.Message || "Failed to upload document");
                }
            } else {
                alert("Failed to upload document due to server error");
            }
        } catch (error) {
            console.error("Error uploading document:", error);
            alert("Network error occurred while uploading document.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSaveTask = async () => {
        if (!taskData.Title || !taskData.SalesActivityType) {
            alert("Title and Activity Type are required");
            return;
        }

        setIsSubmitting(true);
        try {
            const d = new Date(`${taskData.DueDate}T${taskData.DueTime}`);
            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            const formattedEndDate = `${d.getDate().toString().padStart(2, '0')} ${months[d.getMonth()]} ${d.getFullYear()} ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;

            const uid = localStorage.getItem('userId') || '34594';
            const leadId = selectedRecord.EnquiryID || selectedRecord.LeadID || selectedRecord.Id || selectedRecord.text;

            const payload = {
                Token: TOKEN || "-2295521862261168",
                Details: {
                    insertUpdateTask: {
                        Id: 0,
                        Title: taskData.Title,
                        Description: taskData.Description,
                        Remarks: taskData.Remarks,
                        Address: "",
                        StartDate: "",
                        AssignedTo: parseInt(taskData.Owner || uid),
                        BySMS: false,
                        CollabratoerUserIds: Array.isArray(taskData.Collaborators) ? taskData.Collaborators.join('#') : (typeof taskData.Collaborators === 'string' ? taskData.Collaborators.split(',').join('#') : (taskData.Collaborators || "")),
                        CompleteAddress: "",
                        CreatedBy: Number(uid),
                        DLTTemplateIDForNotify: "",
                        DLTTemplateIDForSMSReminder: "",
                        EmailSchedul: "",
                        EmailSchedule: "",
                        EndDate: formattedEndDate,
                        IsActive: true,
                        IsAppointment: false,
                        IsEmail: false,
                        IsSMS: false,
                        IsTask: true,
                        Latitude: "",
                        LeadId: parseInt(leadId),
                        Longitude: "",
                        NotifyMe: false,
                        OutcomesId: 0,
                        SmsSchedule: "",
                        Status: "open",
                        TaskId: parseInt(taskData.SalesActivityType),
                        TaskType: ""
                    },
                    TimeZone: "India Standard Time"
                }
            };

            console.log("Adding Task Payload:", payload);

            const resp = await fetch(`${API_BASE_SERVICES}/${API_ENDPOINTS.TASKS.INSERT_NEW_TASK_OR_APPOINTMENT}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json; charset=UTF-8',
                    'X-Requested-With': 'XMLHttpRequest'
                },
                body: JSON.stringify(payload)
            });

            if (resp.ok) {
                const result = await resp.json();
                const d_res = result.d;
                let isSuccess = false;
                let msg = 'Task saved successfully.';
                if (d_res === "Success" || d_res === "" || (typeof d_res === 'object' && d_res?.IsInserted) || d_res?.Message === "Success") {
                    isSuccess = true;
                } else if (typeof d_res === 'string' && /success|created|saved/i.test(d_res)) {
                    isSuccess = true; msg = d_res;
                } else if (typeof d_res === 'object' && d_res?.Message && /success|created|saved/i.test(d_res.Message)) {
                    isSuccess = true; msg = d_res.Message;
                } else if (typeof result.Message === 'string' && /success|created|saved/i.test(result.Message)) {
                    isSuccess = true; msg = result.Message;
                } else if (result.Status === 1 || result.Status === "1") {
                    isSuccess = true; msg = result.Message || msg;
                }

                if (isSuccess) {
                    showOperationSuccess("Task data saved successfully.");
                    onClose();
                } else {
                    Swal.fire({ icon: 'error', title: 'Error', text: (typeof d_res === 'object' ? d_res?.Message : d_res) || result.Message || 'Failed to save task' });
                    onClose();
                }
            } else {
                Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to save task' });
            }
        } catch (error) {
            console.error('Error saving task:', error);
            Swal.fire({ icon: 'error', title: 'Error', text: 'An error occurred while saving task' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSaveAppointment = async () => {
        setIsSubmitting(true);
        try {
            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

            const sd = new Date(`${appointmentData.StartDate}T${appointmentData.StartTime}`);
            const formattedStartDate = `${sd.getDate().toString().padStart(2, '0')} ${months[sd.getMonth()]} ${sd.getFullYear()} ${sd.getHours().toString().padStart(2, '0')}:${sd.getMinutes().toString().padStart(2, '0')}`;

            const ed = new Date(`${appointmentData.EndDate}T${appointmentData.EndTime}`);
            const formattedEndDate = `${ed.getDate().toString().padStart(2, '0')} ${months[ed.getMonth()]} ${ed.getFullYear()} ${ed.getHours().toString().padStart(2, '0')}:${ed.getMinutes().toString().padStart(2, '0')}`;

            const uid = localStorage.getItem('userId') || '34594';
            const leadId = selectedRecord.EnquiryID || selectedRecord.LeadID || selectedRecord.Id || selectedRecord.text;

            const payload = {
                Token: TOKEN || "-2295521862261168",
                Details: {
                    insertUpdateTask: {
                        Id: 0,
                        Title: appointmentData.Title,
                        Description: appointmentData.Description,
                        Remarks: appointmentData.Remarks,
                        Address: appointmentData.Where,
                        StartDate: formattedStartDate,
                        AssignedTo: parseInt(appointmentData.Owner || uid),
                        BySMS: false,
                        CollabratoerUserIds: Array.isArray(appointmentData.Collaborators) ? appointmentData.Collaborators.join('#') : (typeof appointmentData.Collaborators === 'string' ? appointmentData.Collaborators.split(',').join('#') : (appointmentData.Collaborators || "")),
                        CompleteAddress: appointmentData.Where,
                        CreatedBy: Number(uid),
                        DLTTemplateIDForNotify: "",
                        DLTTemplateIDForSMSReminder: "",
                        EmailSchedul: "",
                        EmailSchedule: "",
                        EndDate: formattedEndDate,
                        IsActive: true,
                        IsAppointment: true,
                        IsEmail: false,
                        IsSMS: false,
                        IsTask: false,
                        Latitude: appointmentData.Latitude?.toString() || "",
                        LeadId: parseInt(leadId),
                        Longitude: appointmentData.Longitude?.toString() || "",
                        NotifyMe: false,
                        OutcomesId: 0,
                        SmsSchedule: "",
                        Status: "open",
                        TaskId: parseInt(appointmentData.SalesActivityType),
                        TaskType: ""
                    },
                    TimeZone: "India Standard Time"
                }
            };

            const resp = await fetch(`${API_BASE_SERVICES}/${API_ENDPOINTS.TASKS.INSERT_NEW_TASK_OR_APPOINTMENT}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json; charset=UTF-8',
                    'X-Requested-With': 'XMLHttpRequest'
                },
                body: JSON.stringify(payload)
            });

            if (resp.ok) {
                const result = await resp.json();
                const d_res = result.d;
                let isSuccess = false;
                let msg = 'Appointment saved successfully.';
                if (d_res === "Success" || d_res === "" || (typeof d_res === 'object' && d_res?.IsInserted)) {
                    isSuccess = true;
                } else if (typeof d_res === 'string' && /success|created|saved/i.test(d_res)) {
                    isSuccess = true; msg = d_res;
                } else if (typeof d_res === 'object' && d_res?.Message && /success|created|saved/i.test(d_res.Message)) {
                    isSuccess = true; msg = d_res.Message;
                } else if (typeof result.Message === 'string' && /success|created|saved/i.test(result.Message)) {
                    isSuccess = true; msg = result.Message;
                } else if (result.Status === 1 || result.Status === "1") {
                    isSuccess = true; msg = result.Message || msg;
                }

                if (isSuccess) {
                    showOperationSuccess("Appointment saved successfully.");
                    onClose();
                } else {
                    alert((typeof d_res === 'object' ? d_res?.Message : d_res) || result.Message || 'Failed to save appointment');
                    onClose();
                }
            } else {
                alert('Failed to save appointment');
            }
        } catch (error) {
            console.error('Error saving appointment:', error);
            alert('An error occurred');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSaveDeal = async () => {
        if (!dealData.Amount) { alert("Enter amount"); return; }
        setIsSubmitting(true);
        try {
            const uid = localStorage.getItem('userId') || '34594';
            const leadId = selectedRecord.EnquiryID || selectedRecord.LeadID || selectedRecord.Id || selectedRecord.text;

            const d = new Date(dealData.ClosureDate);
            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            const fmtD = `${d.getDate().toString().padStart(2, '0')} ${months[d.getMonth()]} ${d.getFullYear()}`;

            const payload = {
                Token: TOKEN || "-2295521862261168",
                Details: {
                    LeadId: leadId.toString(),
                    PipelineId: Number(dealData.PipelineId),
                    PipelineStage: Number(dealData.PipelineStage),
                    Amount: Number(dealData.Amount),
                    ClosureDate: fmtD,
                    DealOwner: Number(dealData.DealOwner || uid),
                    Probability: dealData.Probability.toString(),
                    UserId: Number(uid)
                }
            };

            const resp = await fetch(`${API_BASE_SERVICES}/${API_ENDPOINTS.COMMON.SAVE_DEAL}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json; charset=UTF-8',
                    'X-Requested-With': 'XMLHttpRequest'
                },
                body: JSON.stringify(payload)
            });

            if (resp.ok) {
                const result = await resp.json();
                const d_res = result.d;
                if (d_res === "Success" || d_res === "Record Saved Successfully") {
                    showOperationSuccess('Deal saved successfully.');
                    onClose();
                } else {
                    alert(d_res?.Message || 'Deal saved');
                    onClose();
                }
            } else {
                alert('Failed to save deal');
            }
        } catch (error) {
            console.error('Error saving deal:', error);
            alert('An error occurred');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSaveWebform = async () => {
        if (!selectedWebform) { alert("Select webform"); return; }
        const form = webforms.find(f => f.Id == selectedWebform);
        if (form) {
            const leadId = selectedRecord.EnquiryID || selectedRecord.LeadID || selectedRecord.Id || selectedRecord.text;
            const url = `https://kit19.com/UserCRMCampaign/webFormDetails.aspx?ParentID=34594&cName=${encodeURIComponent(form.Text)}&EntityName=Lead&AgentId=34594&EntityId=${leadId}`;
            window.open(url, '_blank');
            showOperationSuccess('Webform opened successfully.');
            onClose();
        }
    };

    // --- RENDER HELPERS ---

    const getTitle = () => {
        const titles = {
            followup: 'Add Followup',
            mail: 'Send Mail',
            sms: 'Send SMS',
            voice: 'Send Voice',
            note: 'Add Note',
            upload: 'Upload Document',
            addtask: 'Add Task',
            addappointment: 'Add Appointment',
            adddeal: 'Add Deal',
            fillWebform: 'Fill Webform'
        };
        return titles[operationMode] || 'Operation';
    };

    const handleSaveLead = async () => {
        if (!leadForm.PersonName || !leadForm.Mobile1) {
            alert('Name and Mobile are required');
            return;
        }

        setIsSubmitting(true);
        try {
            const { parentId } = getSession();
            const payload = {
                Token: TOKEN || "-2295521862261168",
                Details: {
                    PersonName: leadForm.PersonName,
                    CompanyName: leadForm.CompanyName,
                    MobileNo: `${selectedDialCode} ${leadForm.Mobile1}`,
                    EmailID: leadForm.Email1,
                    SourceName: leadForm.Source || "0",
                    MediumName: leadForm.Medium || "0",
                    CampaignName: leadForm.Campaign || "0",
                    Remark: leadForm.Remarks,
                    ResidentialAddress: leadForm.ResiAddress,
                    OfficialAddress: leadForm.OfficeAddress,
                    PinCode: leadForm.PinCode || '0',
                    EnquiryTags: leadForm.EnquiryTags || "",
                    ParentID: Number(parentId),
                    EnquiryId: 0,
                    ReminderDate: new Date().toISOString().split('T')[0]
                }
            };

            const response = await fetch(`${SERVICE_AZURE_URL}${API_ENDPOINTS.ENQUIRIES.SAVE_ADD_LEAD_OR_MERGE}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                showOperationSuccess('Lead added successfully.');
                onClose();
            } else {
                alert('Failed to add lead');
            }
        } catch (error) {
            console.error('Error adding lead:', error);
            alert('An error occurred');
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderOperationFields = () => {
        if (operationMode === 'addlead') return <AddLeadForm onSaved={() => onClose()} onClose={() => onClose()} />;
        if (operationMode === 'addenquiry') return <AddEnquiryForm onBack={onClose} onSuccess={() => onClose()} />;
        if (!selectedRecord) return null;
        if (isLoadingInitialData) return (
            <div className="flex flex-col items-center py-12 text-gray-400 gap-3">
                <Loader2 className="animate-spin" size={32} />
                <span className="text-sm font-medium">Configuring {getTitle()}...</span>
            </div>
        );

        switch (operationMode) {
            case 'followup': return renderFollowupFields();
            case 'mail': return renderMailFields();
            case 'sms': return renderSmsFields();
            case 'voice': return renderVoiceFields();
            case 'note': return renderNoteFields();
            case 'upload': return renderUploadFields();
            case 'addtask': return renderTaskFields();
            case 'addappointment': return renderAppointmentFields();
            case 'adddeal': return renderDealFields();
            case 'fillWebform': return renderWebformFields();
            default: return null;
        }
    };

    const renderFollowupFields = () => (
        <div className="operation-form-compact">
            <div className="form-group-premium">
                <div className="select-wrapper-themed">
                    <select
                        className="theme-select"
                        value={followupForm.status}
                        onChange={(e) => setFollowupForm(p => ({ ...p, status: e.target.value }))}
                    >
                        <option value="">Select Status</option>
                        {followupStatuses.map((s, i) => <option key={i} value={s.Value || s.Id || s.Text}>{s.Text || s.Name}</option>)}
                    </select>
                    <ChevronDown size={16} className="select-arrow-themed" />
                </div>
            </div>

            <div className="form-group-premium mt-3">
                <div className="select-wrapper-themed">
                    <select
                        className="theme-select"
                        value={followupForm.assignedUser || ''}
                        onChange={(e) => setFollowupForm(p => ({ ...p, assignedUser: e.target.value }))}
                    >
                        <option value="">Assigned To</option>
                        {userHierarchy.map((u, i) => <option key={i} value={u.Code || u.Value || u.Id}>{u.Text || u.Name}</option>)}
                    </select>
                    <ChevronDown size={16} className="select-arrow-themed" />
                </div>
            </div>

            <div className="form-group-premium mt-3">
                <label className="theme-label-premium">Next Followup Date and Time</label>
                <div className="premium-datetime-container" onClick={(e) => e.currentTarget.querySelector('input').showPicker()}>
                    <Calendar size={18} className="datetime-icon" />
                    <div className="premium-datetime-value">
                        {followupForm.dueDate ? formatDateTimeDisplay(followupForm.dueDate.split(' ')[0], followupForm.dueDate.split(' ')[1]) : "--"}
                    </div>
                    <input
                        type="datetime-local"
                        className="hidden-datetime-input"
                        value={followupForm.dueDate ? followupForm.dueDate.replace(' ', 'T') : ''}
                        onChange={(e) => setFollowupForm(p => ({ ...p, dueDate: e.target.value.replace('T', ' ') }))}
                    />
                </div>
            </div>

            <div className="form-group-premium mt-3">
                <label className="theme-label-premium">Remarks</label>
                <textarea
                    className="theme-textarea h-24"
                    placeholder="Enter followup remarks..."
                    value={followupForm.remarks || ''}
                    onChange={(e) => setFollowupForm(p => ({ ...p, remarks: e.target.value }))}
                ></textarea>
            </div>

            <div className="form-group-premium mt-4 relative z-[100]">
                <label className="theme-label-mini">Choose Products *</label>
                <div className="search-input-wrapper-themed mt-1">
                    <input
                        type="text"
                        className="theme-search-input"
                        placeholder="Enter product..."
                        value={productSearch || ''}
                        onChange={(e) => handleProductSearch(e.target.value)}
                    />
                    {isProductLoading ? (
                        <Loader2 className="animate-spin text-gray-400" size={16} />
                    ) : (
                        <Search size={16} className="text-gray-400" />
                    )}
                </div>
                {isProductDropdownOpen && (
                    <div className="suggestions-dropdown-themed">
                        {isProductLoading ? (
                            <div className="searching-themed p-4 text-center">
                                <Loader2 className="animate-spin text-blue-500 mx-auto mb-2" size={24} />
                                <span className="text-sm text-gray-500">Searching products...</span>
                            </div>
                        ) : productSuggestions.length > 0 ? (
                            productSuggestions.map((p, idx) => (
                                <div key={idx} className="suggestion-item-themed" onClick={() => addProduct(p)}>
                                    <div className="product-avatar-themed">
                                        {(p.Image || p.image) ? (
                                            <img src={p.Image || p.image || 'https://via.placeholder.com/40?text=No+Img'} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.src = 'https://via.placeholder.com/40?text=No+Img'; e.target.onerror = null; }} />
                                        ) : (
                                            <Package size={20} />
                                        )}
                                    </div>
                                    <span className="item-title-themed">{p.ProductName || p.Name || p.Text}</span>
                                </div>
                            ))
                        ) : (
                            <div className="no-results-themed p-4 text-center text-sm text-gray-500">
                                No products found for "{productSearch}"
                            </div>
                        )}
                    </div>
                )}
                {selectedProducts.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                        {selectedProducts.map((prod, i) => (
                            <div key={i} className="selected-record-pill-themed">
                                <div className="flex items-center gap-3">
                                    <div className="avatar-square-themed">
                                        {prod.Image ? <img src={prod.Image || 'https://via.placeholder.com/40?text=No+Img'} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.src = 'https://via.placeholder.com/40?text=No+Img'; e.target.onerror = null; }} /> : <Package size={16} />}
                                    </div>
                                    <span className="record-name-themed">{prod.ProductName || prod.Text || prod.Name}</span>
                                </div>
                                <button onClick={() => {
                                    const updated = selectedProducts.filter(x => (x.ProductId || x.Id) !== (prod.ProductId || prod.Id));
                                    setSelectedProducts(updated);
                                    setFollowupForm(prev => ({ ...prev, Products: updated.map(x => x.ProductId || x.Id).join(',') }));
                                }} className="remove-btn-themed"><X size={16} /></button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="form-group-premium mt-4 flex items-center gap-2">
                <input
                    type="checkbox"
                    id="reassign"
                    className="theme-checkbox-native"
                    checked={followupForm.isReAssign}
                    onChange={(e) => setFollowupForm(p => ({ ...p, isReAssign: e.target.checked }))}
                />
                <label htmlFor="reassign" className="m-0 text-sm font-semibold text-gray-600 cursor-pointer">Re-Assign Lead?</label>
            </div>
        </div>
    );

    const renderMailFields = () => (
        <div className="operation-form-compact">
            {/* Choose Emails Checkbox List */}
            <div className="form-group-premium">
                <label className="theme-label-mini">Choose Emails</label>
                <div className="checkbox-cloud mt-2">
                    {leadEmails.length > 0 ? leadEmails.map(email => (
                        <label key={email} className="checkbox-tag">
                            <input
                                type="checkbox"
                                checked={mailData.ChooseEmails.includes(email)}
                                onChange={(e) => {
                                    const val = e.target.checked ? [...mailData.ChooseEmails, email] : mailData.ChooseEmails.filter(x => x !== email);
                                    setMailData(p => ({ ...p, ChooseEmails: val }));
                                }}
                            />
                            <span>{email}</span>
                        </label>
                    )) : (
                        <span className="text-[11px] text-gray-500 italic">No emails found in lead details</span>
                    )}
                </div>
            </div>

            {/* Select Account Dropdown */}
            <div className="form-group-premium mt-4">
                <label className="theme-label-mini">Select Account *</label>
                <div className="select-wrapper-themed mt-1">
                    <select
                        className="theme-select"
                        value={mailData.Account}
                        onChange={(e) => setMailData(p => ({ ...p, Account: e.target.value }))}
                    >
                        <option value="">--Select Account--</option>
                        {mailAccounts.map(acc => (
                            <option key={acc.AccountId || acc.Code} value={acc.AccountId || acc.Code}>
                                {acc.AccountName || acc.Text}
                            </option>
                        ))}
                    </select>
                    <ChevronDown size={14} className="select-icon-themed" />
                </div>
            </div>

            {/* From & Reply To Grid */}
            <div className="grid grid-cols-2 gap-3 mt-3">
                <div className="form-group-premium">
                    <label className="theme-label-mini">From Email *</label>
                    <div className="select-wrapper-themed mt-1">
                        <select
                            className="theme-select"
                            value={mailData.From}
                            onChange={(e) => setMailData(p => ({ ...p, From: e.target.value }))}
                        >
                            <option value="">Select Email</option>
                            {fromMailOptions.map((opt, i) => (
                                <option key={i} value={opt.Value || opt.Name || opt.Text}>
                                    {opt.Text || opt.Name || opt.Value}
                                </option>
                            ))}
                        </select>
                        <ChevronDown size={14} className="select-icon-themed" />
                    </div>
                </div>
                <div className="form-group-premium">
                    <label className="theme-label-mini">Reply To *</label>
                    <div className="select-wrapper-themed mt-1">
                        <select
                            className="theme-select"
                            value={mailData.ReplyTo}
                            onChange={(e) => setMailData(p => ({ ...p, ReplyTo: e.target.value }))}
                        >
                            <option value="">Select Email</option>
                            {fromMailOptions.map((opt, i) => (
                                <option key={i} value={opt.Value || opt.Name || opt.Text}>
                                    {opt.Text || opt.Name || opt.Value}
                                </option>
                            ))}
                        </select>
                        <ChevronDown size={14} className="select-icon-themed" />
                    </div>
                </div>
            </div>

            {/* Mode Switcher Radios */}
            <div className="mt-4 flex flex-col gap-2">
                <label className="flex items-center gap-2 cursor-pointer group">
                    <input
                        type="radio"
                        name="mailMode"
                        className="theme-radio-native"
                        checked={mailData.MailMode === 'template'}
                        onChange={() => setMailData(p => ({ ...p, MailMode: 'template' }))}
                    />
                    <span className="text-[13px] font-medium text-gray-700 group-hover:text-blue-600 transition-colors">Select Template</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer group">
                    <input
                        type="radio"
                        name="mailMode"
                        className="theme-radio-native"
                        checked={mailData.MailMode === 'compose'}
                        onChange={() => setMailData(p => ({ ...p, MailMode: 'compose' }))}
                    />
                    <span className="text-[13px] font-medium text-gray-700 group-hover:text-blue-600 transition-colors">Compose Mail</span>
                </label>
            </div>

            {/* Template Selection / Subjet & Body */}
            {mailData.MailMode === 'template' ? (
                <div className="form-group-premium mt-4">
                    <label className="theme-label-mini">Template</label>
                    <div className="select-wrapper-themed mt-1">
                        <select
                            className="theme-select"
                            value={mailData.TemplateId}
                            onChange={(e) => {
                                const tid = e.target.value;
                                const temp = mailTemplates.find(t => (t.MailTemplateId || t.Id) == tid);
                                setMailData(p => ({
                                    ...p,
                                    TemplateId: tid,
                                    Body: temp?.Body || p.Body,
                                    Subject: temp?.MailTemplateName || temp?.Text || p.Subject
                                }));
                            }}
                        >
                            <option value="">Select Template</option>
                            {mailTemplates.map(t => (
                                <option key={t.MailTemplateId || t.Id} value={t.MailTemplateId || t.Id}>
                                    {t.MailTemplateName || t.Text}
                                </option>
                            ))}
                        </select>
                        <ChevronDown size={14} className="select-icon-themed" />
                    </div>
                </div>
            ) : (
                <>
                    <div className="form-group-premium mt-4">
                        <label className="theme-label-mini">Subject *</label>
                        <input
                            type="text"
                            className="theme-search-input mt-1"
                            value={mailData.Subject}
                            onChange={(e) => setMailData(p => ({ ...p, Subject: e.target.value }))}
                            placeholder="Email subject"
                        />
                    </div>
                    <div className="form-group-premium mt-3">
                        <label className="theme-label-mini">Message Content</label>
                        <textarea
                            rows="6"
                            className="theme-textarea mt-1"
                            value={mailData.Body}
                            onChange={(e) => setMailData(p => ({ ...p, Body: e.target.value }))}
                            placeholder="Compose your mail..."
                        ></textarea>
                    </div>
                </>
            )}
        </div>
    );

    const renderSmsFields = () => (
        <div className="operation-form">
            <div className="form-group-premium">
                <label className="theme-label-mini">Choose Mobile Numbers</label>
                <div className="checkbox-cloud mt-2">
                    {leadMobileNos.length > 0 ? leadMobileNos.map(num => (
                        <label key={num} className="checkbox-tag">
                            <input
                                type="checkbox"
                                checked={smsData.ChooseNumbers.includes(num)}
                                onChange={(e) => {
                                    const val = e.target.checked ? [...smsData.ChooseNumbers, num] : smsData.ChooseNumbers.filter(x => x !== num);
                                    setSmsData(p => ({ ...p, ChooseNumbers: val }));
                                }}
                            />
                            <span>{num}</span>
                        </label>
                    )) : (
                        <span className="text-[11px] text-gray-500 italic">No mobile numbers found</span>
                    )}
                </div>
            </div>
            <div className="form-row-themed grid grid-cols-2 gap-3 mt-4">
                <div className="form-group-premium">
                    <label className="theme-label-mini">Select Account *</label>
                    <div className="select-wrapper-themed mt-1">
                        <select className="theme-select" value={smsData.Account} onChange={(e) => setSmsData(p => ({ ...p, Account: e.target.value }))}>
                            <option value="">Select Account</option>
                            {smsAccounts.map(acc => <option key={acc.AccountId || acc.Code} value={acc.AccountId || acc.Code}>{acc.AccountName || acc.Text}</option>)}
                        </select>
                        <ChevronDown size={14} className="select-arrow-themed" />
                    </div>
                </div>
                <div className="form-group-premium">
                    <label className="theme-label-mini">Sender ID</label>
                    <div className="select-wrapper-themed mt-1">
                        <select className="theme-select" value={smsData.SenderId} onChange={(e) => setSmsData(p => ({ ...p, SenderId: e.target.value }))}>
                            <option value="">Select Sender</option>
                            {smsSenderIds.map((s, i) => <option key={i} value={s.SenderName || s.Code}>{s.SenderName}</option>)}
                        </select>
                        <ChevronDown size={14} className="select-arrow-themed" />
                    </div>
                </div>
            </div>
            <div className="form-group">
                <label>Message *</label>
                <textarea rows="4" value={smsData.Message} onChange={(e) => setSmsData(p => ({ ...p, Message: e.target.value }))} placeholder="Type your SMS..."></textarea>
            </div>
        </div>
    );

    const renderVoiceFields = () => (
        <div className="operation-form-compact">
            <div className="form-group-premium">
                <label className="theme-label-mini">Choose Mobile Numbers</label>
                <div className="checkbox-cloud mt-2">
                    {leadMobileNos.length > 0 ? leadMobileNos.map(num => (
                        <label key={num} className="checkbox-tag">
                            <input
                                type="radio"
                                name="voiceMobile"
                                checked={voiceData.ChooseNumbers.includes(num)}
                                onChange={() => setVoiceData(p => ({ ...p, ChooseNumbers: [num] }))}
                            />
                            <span>{num}</span>
                        </label>
                    )) : (
                        <span className="text-[11px] text-gray-500 italic">No mobile numbers found</span>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-1 gap-3 mt-4">
                <div className="form-group-premium">
                    <label className="theme-label-mini">Route</label>
                    <div className="select-wrapper-themed mt-1">
                        <select
                            className="theme-select"
                            value={voiceData.Route}
                            onChange={(e) => setVoiceData(p => ({ ...p, Route: e.target.value }))}
                        >
                            <option value="Transactional">Transactional</option>
                            <option value="Promotional">Promotional</option>
                        </select>
                        <ChevronDown size={14} className="select-arrow-themed" />
                    </div>
                </div>

                <div className="form-group-premium">
                    <label className="theme-label-mini">Caller ID *</label>
                    <div className="select-wrapper-themed mt-1">
                        <select
                            className="theme-select"
                            value={voiceData.DnidNo}
                            onChange={(e) => setVoiceData(p => ({ ...p, DnidNo: e.target.value }))}
                        >
                            <option value="">Select Caller ID</option>
                            {dniNumbers.map((d, i) => (
                                <option key={i} value={d.Code || d.DnidNo}>
                                    {d.Code || d.DnidNo}
                                </option>
                            ))}
                        </select>
                        <ChevronDown size={14} className="select-arrow-themed" />
                    </div>
                </div>

                <div className="form-group-premium">
                    <label className="theme-label-mini">App Flow *</label>
                    <div className="select-wrapper-themed mt-1">
                        <select
                            className="theme-select"
                            value={voiceData.AppFlow}
                            onChange={(e) => setVoiceData(p => ({ ...p, AppFlow: e.target.value }))}
                        >
                            <option value="">Select App Flow</option>
                            {appFlows.map((a, i) => (
                                <option key={i} value={a.Id || a.AppId}>
                                    {a.Text || a.DisplayName || a.Name}
                                </option>
                            ))}
                        </select>
                        <ChevronDown size={14} className="select-arrow-themed" />
                    </div>
                </div>

                <div className="form-group-premium">
                    <label className="theme-label-mini">Duration (sec)</label>
                    <input
                        type="number"
                        className="theme-search-input mt-1"
                        min="1"
                        max="300"
                        value={voiceData.Duration}
                        onChange={(e) => setVoiceData(p => ({ ...p, Duration: e.target.value }))}
                        placeholder="30"
                    />
                </div>
            </div>
        </div>
    );

    const renderNoteFields = () => (
        <div className="operation-form">
            <div className="form-group">
                <label>Add Notes *</label>
                <textarea rows="8" value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Type your internal notes here..."></textarea>
            </div>
        </div>
    );

    const renderUploadFields = () => (
        <div className="operation-form-compact">
            <div className="action-form-row mb-4">
                <label className="action-form-label text-sm font-medium text-gray-700 block mb-2">Document Title *</label>
                <input type="text" value={documentData.Title} onChange={(e) => setDocumentData(p => ({ ...p, Title: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500" placeholder="e.g. KYC Documents" />
            </div>
            <div className="action-form-row">
                <label className="action-form-label text-sm font-medium text-gray-700 block mb-2">Select Document</label>
                <div className="file-upload-container-boxed p-4 border border-gray-200 rounded-md bg-gray-50">
                    <div className="upload-row flex items-center mb-3">
                        <button type="button" className="btn-browse-modern px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition-colors" onClick={() => setShowMediaLibrary(true)}>
                            Browse Media Library...
                        </button>
                        <input type="text" value={documentData.FileName || documentData.Title} readOnly className="action-input-minimal flex-1 ml-3 px-3 py-2 border border-gray-300 rounded bg-white text-sm outline-none" placeholder="No file selected" />
                    </div>
                    <div className="upload-meta-row flex items-center justify-between mt-2 text-xs">
                        <span className="max-size-text-green text-green-600 font-medium">(Max File Size 5 MB)</span>
                        <span className="supported-formats-link-blue text-blue-500 cursor-pointer hover:underline">Click here to see supported file format</span>
                    </div>
                    <div className="extensions-box-light text-xs text-gray-500 mt-2 p-2 bg-white rounded border border-gray-100">
                        .bmp , .csv , .doc , .docx , .gif , .jpeg , .jpg , .pdf , .png , .ppt , .pptx , .txt , .xls , .xlsx
                    </div>
                </div>
            </div>
            
            {showMediaLibrary && (
                <MediaLibrary
                    show={showMediaLibrary}
                    onclose={() => setShowMediaLibrary(false)}
                    onSelectMedia={handleMediaSelect}
                    filterOption="All"
                    cardType="image"
                    leadId={(selectedRecord?.EnquiryID || selectedRecord?.LeadID || selectedRecord?.Id || selectedRecord?.text || 0)}
                />
            )}
        </div>
    );

    const renderTaskFields = () => (
        <div className="operation-form-compact">
            <div className="form-group-premium">
                <label className="theme-label-mini">Task Title *</label>
                <input
                    type="text"
                    className="theme-search-input mt-1"
                    name="Title"
                    value={taskData.Title}
                    onChange={(e) => setTaskData(p => ({ ...p, Title: e.target.value }))}
                    placeholder="Task title"
                />
            </div>

            <div className="grid grid-cols-2 gap-3 mt-3">
                <div className="form-group-premium">
                    <label className="theme-label-mini">Activity Type *</label>
                    <div className="select-wrapper-themed mt-1">
                        <select
                            className="theme-select"
                            value={taskData.SalesActivityType}
                            onChange={(e) => setTaskData(p => ({ ...p, SalesActivityType: e.target.value }))}
                        >
                            <option value="">Select Activity</option>
                            {salesActivityOptions.map((opt, i) => (
                                <option key={i} value={opt.TasKId || opt.Id || opt.Value || opt.Code}>
                                    {opt.TasKName || opt.Text || opt.Name}
                                </option>
                            ))}
                        </select>
                        <ChevronDown size={14} className="select-arrow-themed" />
                    </div>
                </div>

            <div className="grid grid-cols-1 gap-3 mt-3">
                <div className="form-group-premium">
                    <label className="theme-label-premium">Due Date and Time *</label>
                    <div className="premium-datetime-container" onClick={(e) => e.currentTarget.querySelector('input').showPicker()}>
                        <Calendar size={18} className="datetime-icon" />
                        <div className="premium-datetime-value">
                            {taskData.DueDate ? formatDateTimeDisplay(taskData.DueDate, taskData.DueTime) : "--"}
                        </div>
                        <input
                            type="datetime-local"
                            className="hidden-datetime-input"
                            value={taskData.DueDate && taskData.DueTime ? `${taskData.DueDate}T${taskData.DueTime}` : ''}
                            onChange={(e) => {
                                const [d, t] = e.target.value.split('T');
                                setTaskData(p => ({ ...p, DueDate: d, DueTime: t }));
                            }}
                        />
                    </div>
                </div>
            </div>

                <div className="form-group-premium">
                    <label className="theme-label-mini">Owner *</label>
                    <div className="select-wrapper-themed mt-1">
                        <select
                            className="theme-select"
                            value={taskData.Owner}
                            onChange={(e) => setTaskData(p => ({ ...p, Owner: e.target.value }))}
                        >
                            <option value="">Select Owner</option>
                            {ownerOptions.map((u, i) => (
                                <option key={i} value={u.id || u.UserId || u.Id || u.Code || u.User_ID}>{u.name || u.UserName || u.Text || u.DisplayName || u.Name}</option>
                            ))}
                        </select>
                        <ChevronDown size={14} className="select-arrow-themed" />
                    </div>
                </div>
            </div>

            <div className="form-group-premium mt-3">
                <label className="theme-label-mini">Collaborators</label>
                <div className="select-wrapper-themed mt-1">
                    <select
                        className="theme-select"
                        value={taskData.Collaborators}
                        onChange={(e) => setTaskData(p => ({ ...p, Collaborators: e.target.value }))}
                    >
                        <option value="">Select Collaborator</option>
                        {collaboratorOptions.map((u, i) => (
                            <option key={i} value={u.id || u.UserId || u.Id || u.Code || u.User_ID}>{u.name || u.UserName || u.Text || u.DisplayName || u.Name}</option>
                        ))}
                    </select>
                    <ChevronDown size={14} className="select-arrow-themed" />
                </div>
            </div>

            <div className="form-group-premium mt-3">
                <label className="theme-label-mini">Description</label>
                <textarea
                    className="theme-textarea mt-1"
                    rows="2"
                    value={taskData.Description}
                    onChange={(e) => setTaskData(p => ({ ...p, Description: e.target.value }))}
                    placeholder="Task details..."
                ></textarea>
            </div>

            <div className="form-group-premium mt-3">
                <label className="theme-label-mini">Remarks</label>
                <textarea
                    className="theme-textarea mt-1"
                    rows="2"
                    value={taskData.Remarks}
                    onChange={(e) => setTaskData(p => ({ ...p, Remarks: e.target.value }))}
                    placeholder="Internal remarks..."
                ></textarea>
            </div>
        </div>
    );

    const modernSelect = (label, value, options, onChange, placeholder = "Select...") => (
        <div className="form-group-premium">
            <label className="theme-label-mini">{label}</label>
            <div className="select-wrapper-themed mt-1">
                <select className="theme-select" value={value} onChange={onChange}>
                    <option value="">{placeholder}</option>
                    {options.map((opt, i) => (
                        <option key={i} value={opt.TasKId || opt.Id || opt.Value || opt.Code}>
                            {opt.TasKName || opt.Text || opt.Name}
                        </option>
                    ))}
                </select>
                <ChevronDown size={14} className="select-arrow-themed" />
            </div>
        </div>
    );

    const renderAppointmentFields = () => (
        <div className="operation-form-compact py-4">
            <div className="grid grid-cols-[140px_1fr] gap-x-4 gap-y-5 items-start">
                {/* Title */}
                <label className="text-[13px] font-semibold text-gray-700 pt-3">Title <span className="text-red-500">*</span></label>
                <input
                    type="text"
                    className="theme-search-input"
                    value={appointmentData.Title}
                    onChange={(e) => setAppointmentData(p => ({ ...p, Title: e.target.value }))}
                    placeholder="Enter title"
                />

                {/* Description */}
                <label className="text-[13px] font-semibold text-gray-700 pt-3">Description</label>
                <textarea
                    className="theme-textarea"
                    rows="2"
                    value={appointmentData.Description}
                    onChange={(e) => setAppointmentData(p => ({ ...p, Description: e.target.value }))}
                    placeholder="Start typing the details..."
                />

                {/* Remarks */}
                <label className="text-[13px] font-semibold text-gray-700 pt-3">Remarks</label>
                <textarea
                    className="theme-textarea"
                    rows="2"
                    value={appointmentData.Remarks}
                    onChange={(e) => setAppointmentData(p => ({ ...p, Remarks: e.target.value }))}
                    placeholder="Add any additional remarks..."
                />

                {/* Sales Activity Type */}
                <label className="text-[13px] font-semibold text-gray-700 pt-3">Sales Activity Type <span className="text-red-500">*</span></label>
                <div className="select-wrapper-themed">
                    <select className="theme-select" value={appointmentData.SalesActivityType} onChange={(e) => setAppointmentData(p => ({ ...p, SalesActivityType: e.target.value }))}>
                        <option value="">--Select Activity Type--</option>
                        {salesActivityOptions.map((opt, i) => (
                            <option key={i} value={opt.TasKId || opt.Id}>{opt.TasKName || opt.Text}</option>
                        ))}
                    </select>
                    <ChevronDown size={14} className="select-arrow-themed" />
                </div>

                {/* Start Date and Time */}
                <label className="text-[13px] font-semibold text-gray-700 pt-3">Start Date and Time <span className="text-red-500">*</span></label>
                <div className="premium-datetime-container" onClick={(e) => e.currentTarget.querySelector('input').showPicker()}>
                    <Calendar size={18} className="datetime-icon" />
                    <div className="premium-datetime-value">
                        {appointmentData.StartDate ? formatDateTimeDisplay(appointmentData.StartDate, appointmentData.StartTime) : "--"}
                    </div>
                    <input
                        type="datetime-local"
                        className="hidden-datetime-input"
                        value={appointmentData.StartDate && appointmentData.StartTime ? `${appointmentData.StartDate}T${appointmentData.StartTime}` : ''}
                        onChange={(e) => {
                            const [d, t] = e.target.value.split('T');
                            setAppointmentData(p => ({ ...p, StartDate: d, StartTime: t }));
                        }}
                    />
                </div>

                {/* End Date and Time */}
                <label className="text-[13px] font-semibold text-gray-700 pt-3">End Date and Time <span className="text-red-500">*</span></label>
                <div className="premium-datetime-container" onClick={(e) => e.currentTarget.querySelector('input').showPicker()}>
                    <Calendar size={18} className="datetime-icon" />
                    <div className="premium-datetime-value">
                        {appointmentData.EndDate ? formatDateTimeDisplay(appointmentData.EndDate, appointmentData.EndTime) : "--"}
                    </div>
                    <input
                        type="datetime-local"
                        className="hidden-datetime-input"
                        value={appointmentData.EndDate && appointmentData.EndTime ? `${appointmentData.EndDate}T${appointmentData.EndTime}` : ''}
                        onChange={(e) => {
                            const [d, t] = e.target.value.split('T');
                            setAppointmentData(p => ({ ...p, EndDate: d, EndTime: t }));
                        }}
                    />
                </div>

                {/* Where */}
                <label className="text-[13px] font-semibold text-gray-700 pt-3">Where <span className="text-red-500">*</span></label>
                <input
                    ref={whereInputRef}
                    type="text"
                    className="theme-search-input"
                    value={appointmentData.Where}
                    onChange={(e) => setAppointmentData(p => ({ ...p, Where: e.target.value }))}
                    placeholder="Enter location of appointment"
                />

                {/* Owner */}
                <label className="text-[13px] font-semibold text-gray-700 pt-3">Owner <span className="text-red-500">*</span></label>
                <div className="select-wrapper-themed">
                    <select className="theme-select" value={appointmentData.Owner} onChange={(e) => setAppointmentData(p => ({ ...p, Owner: e.target.value }))}>
                        <option value="">Select Owner</option>
                        {ownerOptions.map((u, i) => (
                            <option key={i} value={u.id || u.UserId || u.Id || u.Code}>{u.name || u.UserName || u.Text || u.DisplayName}</option>
                        ))}
                    </select>
                    <ChevronDown size={14} className="select-arrow-themed" />
                </div>

                {/* Collaborators */}
                <label className="text-[13px] font-semibold text-gray-700 pt-3">Collaborators</label>
                <div className="select-wrapper-themed">
                    <select className="theme-select" value={appointmentData.Collaborators} onChange={(e) => setAppointmentData(p => ({ ...p, Collaborators: e.target.value }))}>
                        <option value="">--Select Collaborator--</option>
                        {collaboratorOptions.map((u, i) => (
                            <option key={i} value={u.id || u.UserId || u.Id || u.Code}>{u.name || u.UserName || u.Text || u.DisplayName}</option>
                        ))}
                    </select>
                    <ChevronDown size={14} className="select-arrow-themed" />
                </div>
            </div>
        </div>
    );

    const renderDealFields = () => (
        <div className="operation-form-compact py-4">
            <div className="grid grid-cols-[140px_1fr] gap-x-4 gap-y-5 items-start">
                {/* Deal Value */}
                <label className="text-[13px] font-semibold text-gray-700 pt-3">Deal value <span className="text-red-500">*</span></label>
                <input
                    type="number"
                    className="theme-search-input"
                    value={dealData.Amount}
                    onChange={(e) => setDealData(p => ({ ...p, Amount: e.target.value }))}
                    placeholder="Please Enter Value"
                />

                {/* Deal Pipeline */}
                <label className="text-[13px] font-semibold text-gray-700 pt-3">Deal pipeline</label>
                <div className="select-wrapper-themed">
                    <select
                        className="theme-select"
                        value={dealData.PipelineId}
                        onChange={(e) => {
                            const pid = e.target.value;
                            setDealData(p => ({ ...p, PipelineId: pid }));
                            fetchDealStages(pid);
                        }}
                    >
                        <option value="">--Select Pipeline--</option>
                        {dealPipelines.map(pipe => (
                            <option key={pipe.id} value={pipe.id}>{pipe.name}</option>
                        ))}
                    </select>
                    <ChevronDown size={14} className="select-arrow-themed" />
                </div>

                {/* Deal Stage */}
                <label className="text-[13px] font-semibold text-gray-700 pt-3">Deal Stage</label>
                <div className="select-wrapper-themed">
                    <select
                        className="theme-select"
                        value={dealData.PipelineStage}
                        onChange={(e) => setDealData(p => ({ ...p, PipelineStage: e.target.value }))}
                    >
                        <option value="">--Select Stage--</option>
                        {dealStages.map(s => (
                            <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                    </select>
                    <ChevronDown size={14} className="select-arrow-themed" />
                </div>

                {/* Probability */}
                <label className="text-[13px] font-semibold text-gray-700 pt-3">Probability (%) <span className="text-red-500">*</span></label>
                <input
                    type="number"
                    className="theme-search-input"
                    value={dealData.Probability}
                    onChange={(e) => setDealData(p => ({ ...p, Probability: e.target.value }))}
                    placeholder="Enter Probability"
                />

                {/* Estimated Closure */}
                <label className="text-[13px] font-semibold text-gray-700 pt-3">Estimated Closure</label>
                <div className="date-input-wrapper-themed">
                    <input
                        type="date"
                        className="theme-date-input"
                        value={dealData.ClosureDate}
                        onChange={(e) => setDealData(p => ({ ...p, ClosureDate: e.target.value }))}
                    />
                    <div className="date-icon-box" style={{ right: '8px' }}><Calendar size={14} /></div>
                </div>

                {/* Sales Owner */}
                <label className="text-[13px] font-semibold text-gray-700 pt-3">Sales owner</label>
                <div className="select-wrapper-themed">
                    <select
                        className="theme-select"
                        value={dealData.DealOwner}
                        onChange={(e) => setDealData(p => ({ ...p, DealOwner: e.target.value }))}
                    >
                        <option value="">--Select Owner--</option>
                        {ownerOptions.map((opt, i) => (
                            <option key={opt.id || i} value={opt.id || opt.UserId || opt.Id || opt.Code}>{opt.name || opt.UserName || opt.Text || opt.DisplayName}</option>
                        ))}
                    </select>
                    <ChevronDown size={14} className="select-arrow-themed" />
                </div>
            </div>
        </div>
    );

    const renderWebformFields = () => (
        <div className="operation-form">
            <div className="form-group">
                <label>Select Webform to Fill *</label>
                <select value={selectedWebform} onChange={(e) => setSelectedWebform(e.target.value)}>
                    <option value="">Select Webform</option>
                    {webforms.map(wf => <option key={wf.Id} value={wf.Id}>{wf.Text}</option>)}
                </select>
            </div>
        </div>
    );

    return (
        <Panels
            isOpen={isOpen}
            onClose={onClose}
            title={getTitle()}
            width={operationMode === 'addenquiry' || operationMode === 'addlead' ? 'min(95%, 1000px)' : '500px'}
            hideHeader={operationMode === 'addenquiry' || operationMode === 'addlead'}
            backTitle="Back"
        >
            <div className="drawer-body-premium" style={{ padding: '0' }}>
                <div className="space-y-6">
                    {/* COMMON PHASE 1: Entity Choice - Hidden for 'addlead' and 'addenquiry' */}
                    {operationMode !== 'addlead' && operationMode !== 'addenquiry' && (
                        <div className="theme-form-section">
                            <div className="form-group-premium">
                                <label className="theme-label-premium">ASSOCIATED TO</label>
                                <div className="radio-group-modern">
                                    {leadEnabled && (
                                        <button className={`radio-item ${associatedTo === 'Lead' ? 'active' : ''}`} onClick={() => setAssociatedTo('Lead')}><div className="radio-circle" /><span>Lead</span></button>
                                    )}
                                    {enquiryEnabled && (
                                        <button className={`radio-item ${associatedTo === 'Enquiry' ? 'active' : ''}`} onClick={() => setAssociatedTo('Enquiry')}><div className="radio-circle" /><span>Enquiry</span></button>
                                    )}
                                </div>
                            </div>

                            <div className="form-group-premium mt-6">
                                <label className="theme-label-premium">RELATED TO</label>
                                {selectedRecord ? (
                                    <div className="selected-record-pill-themed">
                                        <div className="flex items-center gap-3 flex-1">
                                            <div className="avatar-square-themed"><User size={16} /></div>
                                            <div className="flex flex-col">
                                                <span className="record-name-themed">{selectedRecord.PersonName || selectedRecord.Name || selectedRecord.text}</span>
                                                {(selectedRecord.MobileNo || selectedRecord.phone) && <span className="record-subtext-themed">{selectedRecord.MobileNo || selectedRecord.phone}</span>}
                                            </div>
                                        </div>
                                        <button onClick={() => setSelectedRecord(null)} className="remove-btn-themed"><X size={16} /></button>
                                    </div>
                                ) : (
                                    <div className="relative" ref={sidebarSearchRef}>
                                        <div className="search-input-wrapper-themed">
                                            <input type="text" placeholder="Search by Name, Mobile or Company" value={searchQuery} onChange={(e) => handleSearchChange(e.target.value)} className="theme-search-input" />
                                            {isLoadingSuggestions && <Loader2 size={18} className="animate-spin text-gray-400" />}
                                        </div>
                                        {isDropdownOpen && (
                                            <div className="suggestions-dropdown-themed">
                                                {suggestions.map((rec, idx) => (
                                                    <div key={idx} className="suggestion-item-themed" onClick={() => handleSelect(rec)}>
                                                        <div className="product-avatar-themed">
                                                            {rec.Image ? (
                                                                <img src={rec.Image} alt="" className="w-full h-full object-cover" />
                                                            ) : (
                                                                <User size={20} className="text-blue-500" />
                                                            )}
                                                        </div>
                                                        <div className="flex flex-col flex-1">
                                                            <span className="item-title-themed">{rec.Name || rec.PersonName || rec.text}</span>
                                                            <div className="flex items-center gap-3 mt-1">
                                                                {(rec.MobileNo || rec.phone || rec.Mobile) && (
                                                                    <span className="product-price-list flex items-center gap-1">
                                                                        <Phone size={12} className="text-gray-400" />
                                                                        {rec.MobileNo || rec.phone || rec.Mobile}
                                                                    </span>
                                                                )}
                                                                <span className="product-price-list flex items-center gap-1">
                                                                    <MapPin size={12} className="text-gray-400" />
                                                                    ID: {rec.Id || rec.leadId || rec.Id}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* PHASE 2: Mode Specific Fields */}
                    <div className={`${(operationMode !== 'addlead' && operationMode !== 'addenquiry') ? 'pt-6 border-t border-gray-100' : ''}`}>
                        {renderOperationFields()}
                    </div>
                </div>
            </div>

            {operationMode !== 'addenquiry' && operationMode !== 'addlead' && (
                <div className="drawer-footer" style={{ padding: '24px 0 0 0', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                    <button onClick={onClose} className="theme-btn-secondary" style={{ padding: '9px 20px', borderRadius: '6px', border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', fontWeight: '600' }}>Cancel</button>
                    <button
                        onClick={handleSubmit}
                        disabled={(operationMode !== 'addlead' && !selectedRecord) || isSubmitting}
                        className="theme-btn-primary"
                        style={{ padding: '9px 28px', border: 'none', background: '#3b82f6', color: '#fff', borderRadius: '6px', cursor: 'pointer', fontWeight: '700' }}
                    >
                        {isSubmitting ? 'Saving...' : 'SAVE'}
                    </button>
                </div>
            )}
        </Panels>
    );
};
